import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BreadCrumb } from '../components/BreadCrumb';
import MarkdownTable from '../components/MarkdownTable';
import TopicDesc from '../components/TopicDesc';
import parseEvaluationResult from '../utils/evaluationParser';
import ReactMarkdown from 'react-markdown';
import { getChapters } from '../services/api';
import type { Chapter } from '../types/common';
import { downloadCorrectionResult, type ImageData } from '../utils/downloadResultsUtil';
import customLogo from '../assets/imgs/custom-logo.svg';
import { sendCorrectionLog, updateRecordCorrection } from '../services/api';
import { hasEditableStructure, hasCategory } from '../utils/correctionFormat';
import type { TeacherRecordEditPayload } from '../services/api';
import { getUserData } from '../utils/userData';
import {
  RevisionEditButton,
  RevisionActionBar,
  RevisionFoot,
  RevisionScoreInput,
  RevisionTextarea,
  RevisionFieldWrap,
  RevisionPopup,
  PencilIcon,
  type CorrectingResultJson,
  type RevisionDraft,
} from '../components/TeacherRevisionMode';
import { escapeHtml } from '../utils/escapeHtml';
import { buildCorrectingRecordsReturnPath } from '../utils/teacherReturnNav';
import {
  computeSiblingTarget,
  findSiblingIndex,
  shouldShowSwitcher,
  canGoPrev,
  canGoNext,
  uploadModeForItem,
  type FeedbackSiblingItem,
} from '../utils/feedbackSiblingNav';

// 評估結果介面
interface EvaluationCategory {
  name: string;
  score: number;
  maxScore: number;
  comment: string;
  grade?: string;
}

interface EvaluationResult {
  totalScore: number;
  maxPossibleScore: number;
  totalGrade: string;
  overallComment: string;
  suggestions: string[];
  wordCount: string;
  categories: EvaluationCategory[];
  uploadMode: string;
  error: string | null;
  markdownTable?: string;
}

type PrintableCategory = EvaluationCategory & { displayLabel: string };

// 新增 GA 事件追蹤的輔助函數
function useGAEventTracker() {
  const trackEvent = useCallback((eventName: string, eventParams: Record<string, string | number | boolean> = {}) => {
    // 確保 gtag 存在
    if (typeof window !== 'undefined' && 'gtag' in window && typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventParams);
    }
  }, []);

  return { trackEvent };
}

// 錯誤彈窗狀態接口
interface ErrorPopupState {
  show: boolean;
  message: string;
}

export default function Feedback() {
  const navigate = useNavigate();
  const [evaluationData, setEvaluationData] = useState<EvaluationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadMode, setUploadMode] = useState("image"); // 預設為 image 模式
  const [showQuestionPopup, setShowQuestionPopup] = useState(false);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [apiLogged, setApiLogged] = useState(false); // 是否已寫入 correcting-log（避免重複）
  const [errorPopup, setErrorPopup] = useState<ErrorPopupState>({ show: false, message: "" });
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImageUrl, setPopupImageUrl] = useState("");
  // 老師整頁修訂模式:FAB 進入 → 全欄位可編輯 → 儲存一次送出所有變更
  const [revisionMode, setRevisionMode] = useState(false);
  const [revisionDraft, setRevisionDraft] = useState<RevisionDraft | null>(null);
  const [revisionSaving, setRevisionSaving] = useState(false);
  // 自動加總總分的「輕彈」提示(kanban 106732):老師視線在左欄構面輸入框、總分在右欄，
  // 沒有動效他不會抬頭看，會誤以為總分沒跟著改分數動
  const [totalBump, setTotalBump] = useState(false);
  // 同 tick 連點防護:state re-render 前讀到舊值，需用 ref 做同步再入閘
  const revisionSavingRef = useRef(false);
  // 修訂模式站內自製彈窗(取代 SweetAlert):有 onConfirm＝確認型雙鈕、autoCloseMs＝訊息型自動關閉
  const [revisionPopup, setRevisionPopup] = useState<{
    message: string;
    autoCloseMs?: number;
    onConfirm?: () => void;
  } | null>(null);
  // 同章節同篩選的學生清單與當前位置(kanban 106957 R5):供「上/下一位」切換。
  // 清單來自檢視紀錄頁寫入的 sessionStorage,重整後由 workflowResult._id 反查位置。
  const [feedbackSiblings, setFeedbackSiblings] = useState<FeedbackSiblingItem[]>([]);
  const [siblingIndex, setSiblingIndex] = useState(-1);
  const [imageZoomLevel, setImageZoomLevel] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [isImageDragging, setIsImageDragging] = useState(false);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    lastOffset: { x: 0, y: 0 },
    currentOffset: { x: 0, y: 0 },
    hasMoved: false,
  });
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const popupImageRef = useRef<HTMLImageElement | null>(null);
  const { trackEvent } = useGAEventTracker();

  // QA Debug 模式：URL 帶 ?debug=ocr 才顯示 OCR 分析面板（給雅平驗證用）
  const debugOCR = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('debug') === 'ocr';
  }, []);

  // 判斷使用哪種呈現：以總滿分區分舊制(25)與新制(6)
  const isHighScoreScheme =
    evaluationData?.maxPossibleScore === 25 ||
    (evaluationData?.maxPossibleScore || 0) > 10;

  // 產出分類顯示文字：舊制顯示分數/滿分，新制優先等第
  const renderCategoryLabel = useCallback(
    (category: EvaluationCategory) => {
      const grade = category.grade ? String(category.grade).trim() : "";
      const hasScore = typeof category.score === "number" && !Number.isNaN(category.score);
      const hasMax = typeof category.maxScore === "number" && category.maxScore > 0;

      if (isHighScoreScheme) {
        if (hasScore && hasMax) return `${category.score} / ${category.maxScore}分`;
        if (hasScore) return `${category.score}分`;
        return "";
      }

      // 新制（總分 6 等）：優先等第，其次分數
      if (grade) {
        if (hasScore) return `等第：${grade}（${category.score}分）`;
        return `等第：${grade}`;
      }

      if (hasScore) return `${category.score}分`;
      return "";
    },
    [isHighScoreScheme]
  );

  const resetImageInteractionState = useCallback(() => {
    setImageZoomLevel(1);
    setImageOffset({ x: 0, y: 0 });
    setIsImageDragging(false);
    dragStateRef.current.isDragging = false;
    dragStateRef.current.lastOffset = { x: 0, y: 0 };
    dragStateRef.current.currentOffset = { x: 0, y: 0 };
    dragStateRef.current.hasMoved = false;
  }, []);

  const clampImageOffset = useCallback((offset: { x: number; y: number }) => {
    if (imageZoomLevel <= 1) {
      return { x: 0, y: 0 };
    }

    const container = imageContainerRef.current;
    const image = popupImageRef.current;

    if (!container || !image) {
      return offset;
    }

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    const maxOffsetX = Math.max(0, (imageRect.width - containerRect.width) / 2);
    const verticalAllowance = containerRect.height * 0.2;
    const maxOffsetY = Math.max(0, (imageRect.height - containerRect.height) / 2 + verticalAllowance);

    return {
      x: Math.min(Math.max(offset.x, -maxOffsetX), maxOffsetX),
      y: Math.min(Math.max(offset.y, -maxOffsetY), maxOffsetY),
    };
  }, [imageZoomLevel]);

  // 從 sessionStorage 獲取評估結果和課次信息 - 移到前面來定義
  const fetchEvaluationData = useCallback(() => {
    try {
      const workflowResultString = sessionStorage.getItem("workflowResult");
      const uploadModeValue = sessionStorage.getItem("uploadMode") || "image";
      // 獲取課次單元信息
      // const essayUnitValue = sessionStorage.getItem("essayUnit") || "";
      // const selectedTopicId = sessionStorage.getItem("selectedTopic") || "";

      // 設置上傳模式
      setUploadMode(uploadModeValue);

      // 追蹤獲取結果開始事件
      trackEvent('results_loaded', {
        event_category: "data_processing",
        event_label: "workflow_result_parsed",
        upload_mode: uploadModeValue,
      });

      // 使用選擇的題目ID（現在是章節ID） - 暫時註解掉
      /*
      if (selectedTopicId) {
        setUnitId(selectedTopicId);
      } else if (essayUnitValue) {
        // 如果有課次單元名稱，將其設為 unitId 以便顯示
        setUnitId(essayUnitValue);
      }
      */

      if (workflowResultString) {
        const workflowResult = JSON.parse(workflowResultString);

        // 針對新的 API 格式，將 correctingResult 作為評估資料來源
        let evaluationSource = workflowResult;
        if (workflowResult.correctingResult) {
          // 如果有 correctingResult，將其設為 originalResponse 供解析器使用
          evaluationSource = {
            originalResponse: workflowResult.correctingResult
          };
        }

        // 使用通用解析器解析評估結果，傳入上傳模式
        const parsedData = parseEvaluationResult(
          evaluationSource,
          uploadModeValue
        );

        setEvaluationData(parsedData);

        // 追蹤評估結果數據
        trackEvent('essay_score', {
          event_category: "results",
          event_label: `score_${parsedData.totalScore}`,
          score: parsedData.totalScore,
          max_score: parsedData.maxPossibleScore || 25,
        });

        // 追蹤結果錯誤
        if (parsedData.error) {
          trackEvent("result_error", {
            event_category: "error",
            event_label: "ai_processing_failure",
            error_message: parsedData.error,
          });
        }
      } else {
        // 追蹤結果數據缺失
        trackEvent('test_data_used', {
          event_category: "development",
          event_label: "example_response_loaded",
        });
      }
    } catch (error: unknown) {
      console.error("❌ 解析評估結果時出錯:", error);
      // 追蹤評估結果解析錯誤
      trackEvent('parsing_error', {
        event_category: "error",
        event_label: "workflow_result_parse_failed",
        error_message: error instanceof Error ? error.message : "Unknown parsing error",
      });
    } finally {
      setLoading(false);
    }
  }, [trackEvent]);

  // 頁面載入時追蹤頁面瀏覽
  useEffect(() => {
    if (isAuthenticated) {
      // 追蹤頁面瀏覽事件
      trackEvent('page_view', {
        page_title: '批改結果頁面',
        page_location: window.location.href,
        page_path: '/feedback',
      });
    }
  }, [isAuthenticated, trackEvent]);

  // 頁面離開追蹤
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (typeof window !== 'undefined' && 'gtag' in window && typeof window.gtag === 'function') {
        trackEvent("page_exit", {
          page_title: "批改結果頁面",
          time_on_page: Math.round(
            (new Date().getTime() - window.performance.timing.navigationStart) / 1000
          ),
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [trackEvent]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (showImagePopup) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      resetImageInteractionState();

      return () => {
        document.body.style.overflow = previousOverflow;
        dragStateRef.current.isDragging = false;
        dragStateRef.current.hasMoved = false;
      };
    }

    resetImageInteractionState();
  }, [showImagePopup, resetImageInteractionState]);

  // 顯示錯誤訊息的通用函數
  const showError = useCallback((message: string) => {
    setErrorPopup({
      show: true,
      message,
    });
    
    // 追蹤錯誤事件
    trackEvent('result_error_shown', {
      error_message: message
    });
  }, [trackEvent]);

  const handleImageClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (dragStateRef.current.hasMoved) {
      dragStateRef.current.hasMoved = false;
      return;
    }

    if (imageZoomLevel === 1) {
      setImageZoomLevel(1.5);
      return;
    }

    resetImageInteractionState();
  };

  const handleImagePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (imageZoomLevel === 1 || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current.isDragging = true;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.startY = event.clientY;
    dragStateRef.current.lastOffset = imageOffset;
    dragStateRef.current.currentOffset = imageOffset;
    dragStateRef.current.hasMoved = false;
    setIsImageDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleImagePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    event.preventDefault();
    const deltaX = event.clientX - dragStateRef.current.startX;
    const deltaY = event.clientY - dragStateRef.current.startY;

    if (!dragStateRef.current.hasMoved && Math.hypot(deltaX, deltaY) > 3) {
      dragStateRef.current.hasMoved = true;
    }

    const newOffset = {
      x: dragStateRef.current.lastOffset.x + deltaX,
      y: dragStateRef.current.lastOffset.y + deltaY,
    };

    const clampedOffset = clampImageOffset(newOffset);

    dragStateRef.current.currentOffset = clampedOffset;
    setImageOffset(clampedOffset);
  };

  const handleImagePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    dragStateRef.current.isDragging = false;
    dragStateRef.current.lastOffset = dragStateRef.current.currentOffset;
    setIsImageDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleImagePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    dragStateRef.current.isDragging = false;
    dragStateRef.current.lastOffset = dragStateRef.current.currentOffset;
    dragStateRef.current.hasMoved = false;
    setIsImageDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  // 檢查使用者登入狀態
  useEffect(() => {
    const checkUserAuthentication = () => {
      const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
      
      if (!userDataString) {
        // 追蹤未登入訪問結果頁面
        trackEvent("unauthenticated_access", {
          event_category: "authentication",
          event_label: "redirect_to_login_from_results",
          page: "results_page",
        });
        
        showError("請先登入以查看批改結果");
        // 設置登入後的目標頁面
        sessionStorage.setItem("intendedDestination", "/feedback");
        // 延遲跳轉，確保錯誤提示能顯示
        setTimeout(() => {
          navigate("/");
        }, 2000);
        return;
      }

      try {
        const userData = JSON.parse(userDataString);
        // const userName = userData.profile?.name || "用戶";
        const userRole = userData.profile?.role || userData.profile?.['custom:role'] || "學生";
        
        // 允許老師和學生都能查看批改結果
        setIsAuthenticated(true);
        
        // 追蹤已授權訪問
        trackEvent("authenticated_access", {
          event_category: "authentication",
          event_label: `${userRole}_access_results`,
          page: "results_page",
        });

        // 如果已登入，且沒有工作流程結果，也導回上傳頁面
        const workflowResult = sessionStorage.getItem("workflowResult");
        
        if (!workflowResult) {
          // 追蹤無結果資料
          trackEvent("missing_data", {
            event_category: "navigation",
            event_label: "no_workflow_result",
            redirect: "to_upload_page",
          });

          // 根據用戶角色決定返回頁面
          if (userRole === "老師") {
            navigate("/upload-essay");
          } else {
            navigate("/student-home");
          }
        }
      } catch (error) {
        console.error("解析用戶數據失敗:", error);
        showError("驗證失敗，請重新登入");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }

      // 完成身份驗證檢查後再進行數據加載
      if (userDataString) {
        try {
          JSON.parse(userDataString);
          // const userData = JSON.parse(userDataString);
          // const userRole = userData.profile?.role || userData.profile?.['custom:role'] || "學生";
          // 無論是老師還是學生都需要載入評估資料
          fetchEvaluationData();
        } catch (error) {
          console.error("解析用戶數據時出錯:", error);
        }
      }
    };

    checkUserAuthentication();
  }, [navigate, showError, trackEvent, fetchEvaluationData]);


  // 批改結果鏡一份到 correcting-log（自助查看儀表板 /viewer 的資料來源）。
  // 高中 2025-07 改版成 Vite 時漏接此寫入，導致儀表板高中資料自此全空 → 補回。
  // 注意：這跟 sendCorrectionRecord（寫高中自家後端 /correction-record/）是兩條獨立的紀錄。
  useEffect(() => {
    if (!(evaluationData && !apiLogged && isAuthenticated)) return;

    const logCorrectionResult = async () => {
      try {
        const workflowResultString = sessionStorage.getItem("workflowResult") || "";

        // input：text 模式帶作文全文，image 模式留空（OCR 前無純文字）。
        let inputText = "";
        if (uploadMode === "text") {
          inputText = sessionStorage.getItem("essayText") || "";
        }

        // picurl：高中原圖網址在 workflowResult，以逗號分隔；
        // correcting-log / 儀表板用 ';' 分隔多圖（見 pics.py split_pics）→ 轉成 ';'。
        let picurl = "";
        try {
          const wf = JSON.parse(workflowResultString || "{}");
          const raw = wf.studentInputOriginUrl || wf.studentInputUrl || "";
          picurl = String(raw)
            .split(",")
            .map((u: string) => u.trim())
            .filter((u: string) => u)
            .join(";");
        } catch {
          // workflowResult 非 JSON → 無圖，picurl 留空
        }

        const result = await sendCorrectionLog({
          userID: getUserData().sub || "",
          input: inputText,
          output: workflowResultString, // 原始未處理的 AI 回應
          totalScore: parseFloat(String(evaluationData?.totalScore)) || 0,
          picurl,
        });

        if (result.success) {
          if (result.data && (result.data as { _id?: string })._id) {
            sessionStorage.setItem("correctionRecordId", (result.data as { _id: string })._id);
          }
          setApiLogged(true);
          trackEvent("correction_log_api_success", {
            event_category: "api",
            event_label: "批改記錄寫入 correcting-log 成功",
            record_id: (result.data as { _id?: string })?._id || "未知",
          });
        } else {
          console.error("批改記錄寫入 correcting-log 失敗:", result.error);
          trackEvent("correction_log_api_error", {
            event_category: "api",
            event_label: "批改記錄寫入 correcting-log 失敗",
            error: result.error?.message || "未知錯誤",
          });
        }
      } catch (error: unknown) {
        console.error("寫入 correcting-log 時出錯:", error);
        trackEvent("correction_log_api_exception", {
          event_category: "api",
          event_label: "批改記錄寫入 correcting-log 異常",
          error_message: (error as Error)?.message || "未知錯誤",
        });
      }
    };

    logCorrectionResult();
  }, [evaluationData, apiLogged, isAuthenticated, uploadMode, trackEvent]);

  // 根據等級獲取顏色
  const getGradeColor = (grade: string): string => {
    if (grade === "A+") return "#0057B7"; // 深藍色（為 A+ 設置特殊顏色）
    if (grade === "A" || grade === "A-") return "#2D86FF"; // 藍色
    if (grade === "B+" || grade === "B") return "#3B82F6"; // 淺藍色
    if (grade === "C+" || grade === "C") return "#F59E0B"; // 黃色
    if (grade === "D+" || grade === "D") return "#EF4444"; // 紅色
    return "#6B7280"; // 灰色 (預設)
  };

  // 處理點擊麵包屑的上傳作文鏈接
  const handleBreadcrumbClick = () => {
    // 追蹤麵包屑導航點擊
    trackEvent('breadcrumb_navigation', {
      event_category: "navigation",
      event_label: "return_to_upload_from_results",
      destination: "/upload-essay",
    });
    
    // 從 workflowResult 獲取 bookId，同樣的邏輯
    const workflowResultString = sessionStorage.getItem("workflowResult");
    let bookId = '';
    let bookName = '';
    
    if (workflowResultString) {
      try {
        const workflowResult = JSON.parse(workflowResultString);
        bookId = workflowResult.bookID || '';
        bookName = workflowResult.bookID || '';
      } catch (error) {
        console.error('解析 workflowResult 失敗:', error);
      }
    }
    
    // 建構帶參數的 URL
    if (bookId) {
      navigate(`/upload-essay?bookId=${encodeURIComponent(bookId)}&bookName=${encodeURIComponent(bookName)}`);
    } else {
      navigate("/student-home");
    }
  };

  // 處理點擊 Logo 回到首頁
  const handleLogoClick = () => {
    // 追蹤 Logo 點擊
    trackEvent('logo_click', {
      event_category: "navigation",
      event_label: "return_to_home_from_results",
    });
    navigate("/"); // 導航到首頁
  };

  // 老師檢視批改記錄時的可編輯資訊(kanban 106732)
  // 需同時滿足:teacherRecord 檢視模式、workflowResult 有紀錄 _id、
  // correctingResult 可解析為含「整體評分」的 JSON(舊文字格式不支援編輯)
  const getTeacherEditContext = useCallback((): {
    recordId: string;
    correctingResult: CorrectingResultJson;
  } | null => {
    try {
      if (sessionStorage.getItem("viewMode") !== "teacherRecord") return null;
      const workflowResultString = sessionStorage.getItem("workflowResult");
      if (!workflowResultString) return null;
      const workflowResult = JSON.parse(workflowResultString);
      if (!workflowResult._id || typeof workflowResult.correctingResult !== "string") {
        return null;
      }
      const cleaned = workflowResult.correctingResult.replace(/```json\n|\n```/g, "");
      const parsed = JSON.parse(cleaned);
      // 兩種格式都可編輯(kanban 1000517):原本只認頂層「整體評分」,新格式的紀錄
      // 會靜默沒有編輯入口 —— 老師只是看不到按鈕、沒有任何提示,很難發現。
      if (!hasEditableStructure(parsed)) return null;
      return { recordId: workflowResult._id, correctingResult: parsed };
    } catch {
      return null;
    }
  }, []);

  // 依當下畫面資料組出修訂模式的基準值:進入修訂時當草稿初始值、儲存時當 diff 基準
  // (只收錄 correctingResult JSON 內存在的評分項，與舊版可編輯範圍一致)
  const buildRevisionBaseline = useCallback((): RevisionDraft | null => {
    const ctx = getTeacherEditContext();
    if (!ctx || !evaluationData) return null;
    const catScores: Record<string, string> = {};
    const catComments: Record<string, string> = {};
    for (const category of evaluationData.categories || []) {
      if (category.name === "整體評分") continue;
      // 新格式的四構面在 categories 陣列裡,不是頂層鍵(kanban 1000517)
      if (!hasCategory(ctx.correctingResult, category.name)) continue;
      catScores[category.name] =
        typeof category.score === "number" && !Number.isNaN(category.score)
          ? String(category.score)
          : "";
      // 文字欄位一律 trim:儲存時 diff 是拿「草稿 trim 後」比基準，
      // 基準不 trim 的話 DB 文字帶頭尾空白時「沒改也會判為變更」(比對兩邊要對稱)
      catComments[category.name] = (category.comment || "").trim();
    }
    return {
      total:
        evaluationData.totalScore !== undefined && evaluationData.totalScore !== null
          ? String(evaluationData.totalScore)
          : "",
      overall: (evaluationData.overallComment || "").trim(),
      suggestions: (evaluationData.suggestions || []).map((s) => s.trim()),
      catScores,
      catComments,
    };
  }, [getTeacherEditContext, evaluationData]);

  // 這份紀錄的總分能不能靠各項得分自動加總(kanban 106732)
  // 只有「整體滿分 == 各評分項滿分之和」時，總分＝各項加總在配分表上才是定義成立的，
  // 這時才把總分改成唯讀自動加總；六級分制(整體滿分 6)或滿分對不上的變體(整體 21、各項合計 24)
  // 一律維持手動可編輯，否則會把總分鎖在一個算錯的數字上。
  // maxScore 缺值/為 0 代表算不出可信的滿分和 → 保守落到手動(寧可多一個可編輯欄位)。
  // 🔴 刻意不重用 isHighScoreScheme:它只看 maxPossibleScore > 10，滿分 21 的變體會被判成 true。
  const canAutoSumTotal = useMemo(() => {
    if (!evaluationData) return false;
    const scoreCategories = (evaluationData.categories || []).filter(
      (category) => category.name !== "整體評分"
    );
    if (scoreCategories.length === 0) return false;
    // 每個評分項都必須真的可編輯(在 correctingResult 裡是物件)才准自動加總:
    // 少了這道，某項在資料裡是字串時畫面不會給它輸入框，加總卻拿 score=0 去算，
    // 儲存就會用一個偏低的總分覆蓋掉原本正確的值，而老師沒有任何欄位能救(kanban 106732 審碼)
    const editableSource = getTeacherEditContext()?.correctingResult;
    if (!editableSource) return false;
    let catMaxSum = 0;
    for (const category of scoreCategories) {
      // 兩種格式都要判得出來(kanban 1000517),否則新格式一律不自動加總
      if (!hasCategory(editableSource, category.name)) return false;
      const maxScore = Number(category.maxScore);
      if (!Number.isFinite(maxScore) || maxScore <= 0) return false;
      catMaxSum += maxScore;
    }
    return catMaxSum === evaluationData.maxPossibleScore;
  }, [evaluationData, getTeacherEditContext]);

  // 各評分項得分加總:修訂模式吃草稿(邊改邊算)、檢視模式吃解析結果
  // 🔴 空值不可當 0 加(Number("") === 0):老師清空 10 分項時會算出一個「看起來很合理」的總分，
  // 缺資料反而被藏起來 → 逐項檢查空白/NaN，命中就回 null 代表「算不出來」。
  // 加總 round 到兩位:不 round 會出現 22.299999999999997 撐爆總分版位。
  const derivedTotal = useMemo<number | null>(() => {
    if (!evaluationData) return null;
    const scoreCategories = (evaluationData.categories || []).filter(
      (category) => category.name !== "整體評分"
    );
    if (scoreCategories.length === 0) return null;
    const draft = revisionMode ? revisionDraft : null;
    let sum = 0;
    for (const category of scoreCategories) {
      const raw =
        draft && draft.catScores[category.name] !== undefined
          ? draft.catScores[category.name]
          : category.score;
      const text = String(raw ?? "").trim();
      const value = Number(text);
      if (text === "" || !Number.isFinite(value)) return null;
      // 超出該項配分範圍(手打 -1、1e2)時也算「算不出來」:否則大字會顯示一個不可能的總分
      // (例如滿分 25 卻顯示 109)，老師會以為系統算錯而不是自己填錯(kanban 106732 審碼)
      const catMax = Number(category.maxScore);
      if (value < 0) return null;
      if (Number.isFinite(catMax) && catMax > 0 && value > catMax) return null;
      sum += value;
    }
    return Math.round(sum * 100) / 100;
  }, [evaluationData, revisionMode, revisionDraft]);

  // 顯示用字串:整數不補 .0、算不出來時佔位 --(字級字重不變、版位不塌)
  const derivedTotalDisplay = derivedTotal === null ? "--" : String(derivedTotal);

  // 加總值一變就縮放一下再回原大小;cleanup 一定要 clearTimeout，
  // 否則老師連打分數時殘留的 timer 會把縮放狀態卡住。
  // 只在「自動加總的修訂模式」跑:這顆元件 3000 行，學生與檢視模式沒必要為看不見的
  // 縮放狀態多兩輪 render(kanban 106732 審碼)
  useEffect(() => {
    if (!revisionMode || !canAutoSumTotal) {
      setTotalBump(false);
      return;
    }
    setTotalBump(true);
    const timer = setTimeout(() => setTotalBump(false), 220);
    return () => clearTimeout(timer);
  }, [derivedTotalDisplay, revisionMode, canAutoSumTotal]);

  // 進入修訂模式:草稿從畫面現值初始化
  const enterRevision = useCallback(() => {
    const baseline = buildRevisionBaseline();
    if (!baseline) return;
    setRevisionDraft(baseline);
    setRevisionMode(true);
    trackEvent("teacher_revision_entered", {
      event_category: "teacher_edit",
      event_label: "revision_mode",
    });
  }, [buildRevisionBaseline, trackEvent]);

  // 取消:整批放棄改動、回檢視模式(零 API 呼叫)
  const cancelRevision = useCallback(() => {
    setRevisionMode(false);
    setRevisionDraft(null);
  }, []);

  // 更新草稿單一欄位的小工具
  const updateRevisionDraft = useCallback((patch: (draft: RevisionDraft) => RevisionDraft) => {
    setRevisionDraft((current) => (current ? patch(current) : current));
  }, []);

  // 即時驗證:只驗「有改動」的欄位(分數範圍/必填)，建議列一律不可空白
  // 有任何錯誤時儲存鈕 disabled＋錯誤就地標示(沿用彈窗版驗證文案)
  const revisionErrors = useMemo<Record<string, string>>(() => {
    if (!revisionMode || !revisionDraft || !evaluationData) return {};
    const baseline = buildRevisionBaseline();
    if (!baseline) return {};
    const errors: Record<string, string> = {};

    // 可自動加總時總分不是使用者輸入 → 不驗證(被清空的那個構面自己會在原地紅字報錯，
    // 總分再紅一次是同一個問題罵兩遍)
    if (!canAutoSumTotal && revisionDraft.total !== baseline.total) {
      const max = evaluationData.maxPossibleScore || 25;
      const total = Number(revisionDraft.total);
      if (revisionDraft.total.trim() === "" || Number.isNaN(total)) {
        errors["total"] = "總分必須是數字";
      } else if (total < 0 || total > max) {
        errors["total"] = `總分需介於 0 到 ${max} 之間`;
      }
    }

    for (const [name, value] of Object.entries(revisionDraft.catScores)) {
      // 自動加總時「沒改過的欄位」也要驗:AI 若給出超過該項滿分的得分(例如題旨發揮 11/10)，
      // 老師只改一句評語就存 → 加總 26 > 滿分 25 被後端擋，而總分已是唯讀、他沒有欄位可救，
      // 整筆紀錄就再也存不了任何東西。全部驗過才會讓超標那格就地紅字、改得動(kanban 106732 審碼)
      if (!canAutoSumTotal && value === baseline.catScores[name]) continue;
      const category = evaluationData.categories?.find((c) => c.name === name);
      const maxScore = category?.maxScore || 0;
      const score = Number(value);
      if (value.trim() === "" || Number.isNaN(score)) {
        errors[`cat-score:${name}`] = `「${name}」得分必須是數字`;
      } else if (score < 0) {
        errors[`cat-score:${name}`] = `「${name}」得分不可為負數`;
      } else if (maxScore > 0 && score > maxScore) {
        // maxScore 取不到(0/undefined)時不做前端上限驗證，交後端白名單擋(422 detail 會顯示)
        errors[`cat-score:${name}`] = `「${name}」得分需介於 0 到 ${maxScore} 之間`;
      }
    }

    for (const [name, value] of Object.entries(revisionDraft.catComments)) {
      if (!value.trim() && (baseline.catComments[name] || "").trim()) {
        errors[`cat-comment:${name}`] = "評語內容不可為空";
      }
    }

    if (!revisionDraft.overall.trim() && baseline.overall.trim()) {
      errors["overall"] = "評語內容不可為空";
    }

    revisionDraft.suggestions.forEach((suggestion, index) => {
      if (!suggestion.trim()) {
        errors[`sug:${index}`] = "建議內容不可為空，若不需要請按 × 刪除";
      }
    });

    return errors;
  }, [revisionMode, revisionDraft, evaluationData, buildRevisionBaseline, canAutoSumTotal]);

  const hasRevisionErrors = Object.keys(revisionErrors).length > 0;

  // 修訂中是否有未儲存變更(kanban 106957 R5):草稿進入時 = baseline,兩者 JSON 相等即無變更。
  // 用途:切換上/下一位學生前的守衛 — 有變更才彈確認,沒變更靜默離開修訂再切。
  // (文字欄位 baseline 已 trim、草稿未 trim,可能把「打了又刪回原樣」判為 dirty;
  //  多問一次確認是安全側,不會誤丟資料。)
  const revisionDirty = useMemo(() => {
    if (!revisionMode || !revisionDraft) return false;
    const baseline = buildRevisionBaseline();
    if (!baseline) return false;
    return JSON.stringify(revisionDraft) !== JSON.stringify(baseline);
  }, [revisionMode, revisionDraft, buildRevisionBaseline]);

  // 儲存:diff 草稿與基準，一次送出所有改動欄位(單一 PUT、只帶有變更的欄位——後端支援多欄位部分更新)
  const saveRevision = useCallback(async () => {
    // 再入閘:同 tick 連點時 revisionSaving state 尚未 re-render、讀到舊值擋不住 → 用 ref 同步判斷
    if (revisionSavingRef.current) return;
    const ctx = getTeacherEditContext();
    const baseline = buildRevisionBaseline();
    if (!ctx || !baseline || !revisionDraft || hasRevisionErrors) return;

    // 組 payload:只放跟基準不同的欄位
    const overallPatch: { 總分?: number; 評語?: string; 建議?: string[] } = {};
    if (canAutoSumTotal) {
      // 可自動加總時總分不吃輸入框，改以各項加總回寫(後端收到 總分 會自動重算等第)
      // baseline 為空字串代表原本沒有總分 → 一律當有變更補上
      const baselineTotalText = baseline.total.trim();
      if (
        derivedTotal !== null &&
        (baselineTotalText === "" || Number(baselineTotalText) !== derivedTotal)
      ) {
        overallPatch.總分 = derivedTotal;
      }
    } else if (revisionDraft.total !== baseline.total) {
      overallPatch.總分 = Number(revisionDraft.total);
    }
    if (revisionDraft.overall.trim() !== baseline.overall) {
      overallPatch.評語 = revisionDraft.overall.trim();
    }
    const trimmedSuggestions = revisionDraft.suggestions.map((s) => s.trim());
    if (JSON.stringify(trimmedSuggestions) !== JSON.stringify(baseline.suggestions)) {
      overallPatch.建議 = trimmedSuggestions;
    }
    const categoriesPatch: NonNullable<TeacherRecordEditPayload["categories"]> = {};
    for (const [name, value] of Object.entries(revisionDraft.catScores)) {
      if (value !== baseline.catScores[name]) {
        categoriesPatch[name] = { ...categoriesPatch[name], 得分: Number(value) };
      }
    }
    for (const [name, value] of Object.entries(revisionDraft.catComments)) {
      if (value.trim() !== baseline.catComments[name]) {
        categoriesPatch[name] = { ...categoriesPatch[name], 評語: value.trim() };
      }
    }

    const changes: Omit<TeacherRecordEditPayload, "teacherId"> = {};
    if (Object.keys(overallPatch).length > 0) changes.整體評分 = overallPatch;
    if (Object.keys(categoriesPatch).length > 0) changes.categories = categoriesPatch;

    // 沒有任何變更 → 不打 API，直接回檢視模式
    if (Object.keys(changes).length === 0) {
      cancelRevision();
      setRevisionPopup({ message: "沒有變更", autoCloseMs: 1500 });
      return;
    }

    revisionSavingRef.current = true;
    setRevisionSaving(true);
    try {
      const { sub } = getUserData();
      const response = await updateRecordCorrection(ctx.recordId, {
        teacherId: sub,
        ...changes,
      });
      // 後端回應缺 correctingResult 或 sessionStorage 同步失敗時，
      // 畫面(fetchEvaluationData 讀 sessionStorage)會顯示舊資料 → 改彈警告而非「已儲存」
      let syncOk = false;
      try {
        const updatedCorrectingResult = response.data?.correctingResult || "";
        if (updatedCorrectingResult) {
          const workflowResultString = sessionStorage.getItem("workflowResult");
          if (workflowResultString) {
            const workflowResult = JSON.parse(workflowResultString);
            workflowResult.correctingResult = updatedCorrectingResult;
            sessionStorage.setItem("workflowResult", JSON.stringify(workflowResult));
            syncOk = true;
            // 同步更新學生清單中該筆(kanban 106957 R5,比照國中儲存後回寫 siblings):
            // 以 _id 比對而非當前 index,任何時序都安全。之後切走再切回顯示的是新值。
            const recordId = ctx.recordId;
            setFeedbackSiblings((prev) =>
              prev.map((s) =>
                s._id === recordId
                  ? { ...s, correctingResult: updatedCorrectingResult }
                  : s
              )
            );
            try {
              const sibRaw = sessionStorage.getItem("feedbackSiblings");
              if (sibRaw) {
                const sibs = JSON.parse(sibRaw);
                if (Array.isArray(sibs)) {
                  const nextSibs = sibs.map((s: FeedbackSiblingItem) =>
                    s._id === recordId
                      ? { ...s, correctingResult: updatedCorrectingResult }
                      : s
                  );
                  sessionStorage.setItem("feedbackSiblings", JSON.stringify(nextSibs));
                }
              }
            } catch (e) {
              console.warn("[feedback siblings] 更新 sessionStorage 失敗", e);
            }
          }
        }
      } catch (error) {
        console.error("更新編輯後批改結果失敗:", error);
      }
      setRevisionMode(false);
      setRevisionDraft(null);
      fetchEvaluationData();
      trackEvent("teacher_edit_saved", {
        event_category: "teacher_edit",
        event_label: "revision_mode_batch",
      });
      if (syncOk) {
        setRevisionPopup({ message: "已儲存", autoCloseMs: 1500 });
      } else {
        setRevisionPopup({
          message: "已儲存，但畫面同步失敗，請重新整理頁面確認結果",
        });
      }
    } catch (error: unknown) {
      // 儲存失敗:站內自製彈窗顯示後端 detail、停留在修訂模式(不掉改動)
      const axiosError = error as {
        response?: { data?: { detail?: unknown } };
        message?: string;
      };
      const detail = axiosError?.response?.data?.detail || axiosError?.message;
      setRevisionPopup({
        message: `儲存失敗：${typeof detail === "string" && detail ? detail : "請稍後再試"}`,
      });
    } finally {
      revisionSavingRef.current = false;
      setRevisionSaving(false);
    }
  }, [
    getTeacherEditContext,
    buildRevisionBaseline,
    revisionDraft,
    hasRevisionErrors,
    cancelRevision,
    fetchEvaluationData,
    trackEvent,
    canAutoSumTotal,
    derivedTotal,
  ]);

  // ── 上/下一位學生切換(kanban 106957 R5) ──────────────────────────────
  // 高中批改細節頁沒有國中的 applyBatchItem;畫面各處直接讀 sessionStorage 的
  // workflowResult 渲染(學生資訊/題目/圖片),評分則由 fetchEvaluationData 解析。
  // 因此「套用一筆」= 覆寫 sessionStorage(workflowResult+uploadMode)後重跑解析,
  // 再由 state 變更觸發 re-render 讓那些 inline 讀取拿到新值。這就是等同 applyBatchItem
  // 的單一套用函式。重整可存活:workflowResult 已寫回 sessionStorage。
  const applyFeedbackItem = useCallback(
    (item: FeedbackSiblingItem) => {
      sessionStorage.setItem("workflowResult", JSON.stringify(item));
      sessionStorage.setItem("uploadMode", uploadModeForItem(item));
      // 殘留彈窗/放大狀態關掉,避免看到上一位的題目或圖片
      setShowQuestionPopup(false);
      setCurrentChapter(null);
      setShowImagePopup(false);
      // 重新解析評分結果(讀 sessionStorage)→ setEvaluationData 觸發整頁重繪,
      // teacherEditCtx 也會跟著以新 workflowResult 重算(修訂 FAB 狀態正確)。
      fetchEvaluationData();
    },
    [fetchEvaluationData]
  );

  const goToSibling = useCallback(
    (delta: number) => {
      const target = computeSiblingTarget(feedbackSiblings, siblingIndex, delta);
      if (!target) return; // 首尾越界:直接不動(按鈕本身也已 disabled,此為縱深防禦)
      applyFeedbackItem(target.item);
      setSiblingIndex(target.index);
    },
    [feedbackSiblings, siblingIndex, applyFeedbackItem]
  );

  // 修訂中切換要先確認(未儲存會遺失);沒未存變更則靜默離開修訂再切,比照國中 guarded。
  const guardedGoToSibling = (delta: number) => {
    if (revisionSaving) return;
    if (revisionMode && revisionDirty) {
      setRevisionPopup({
        message: "尚未儲存的修改將會遺失，確定要切換學生嗎？",
        onConfirm: () => {
          cancelRevision();
          goToSibling(delta);
        },
      });
      return;
    }
    if (revisionMode) cancelRevision();
    goToSibling(delta);
  };

  // 掛載時載入同章節學生清單(檢視紀錄頁寫入),並用當前 workflowResult._id 反查位置。
  // 找不到(當前筆不屬於這份清單/舊入口殘留)→ 視為無清單,維持單筆、不顯示切換。
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("feedbackSiblings");
      if (!raw) return;
      const list = JSON.parse(raw);
      if (!Array.isArray(list) || list.length === 0) return;
      const wfString = sessionStorage.getItem("workflowResult");
      const currentId = wfString ? JSON.parse(wfString)?._id ?? null : null;
      const idx = findSiblingIndex(list, currentId);
      if (idx < 0) return;
      setFeedbackSiblings(list);
      setSiblingIndex(idx);
    } catch (e) {
      console.warn("[feedback siblings] 解析清單失敗", e);
    }
  }, []);

  // 處理點擊"再改一篇"或"返回"按鈕
  const handleRetryClick = () => {
    const viewMode = sessionStorage.getItem("viewMode");
    
    if (viewMode === "teacherRecord") {
      // 從教師記錄頁面進入，返回到批改記錄頁面
      trackEvent('return_to_records_click', {
        event_category: "navigation",
        event_label: "從批改結果返回記錄頁面",
        from_page: "批改結果頁面",
      });
      
      // 清除 viewMode 標記
      sessionStorage.removeItem("viewMode");
      // 帶回原本在看的書/題(kanban 106957):正常返回時 teacherState 一路存活到返回那次
      // ClassFeedback 初載才被消耗,會優先還原章節(含下拉),題目本來就會回來;這段 query
      // 是冗餘保險,只有 teacherState 缺席時(最典型:返回後又重新整理檢視紀錄頁)才生效,
      // 用來避免落回「未選題」的全章節列表。workflowResult 缺席/壞資料時退回裸路徑。
      navigate(
        buildCorrectingRecordsReturnPath(
          sessionStorage.getItem("workflowResult")
        )
      );
    } else if (viewMode === "studentRecord") {
      // 從學生記錄頁面進入，返回到學生首頁
      trackEvent('return_to_student_home_click', {
        event_category: "navigation",
        event_label: "從批改結果返回學生首頁",
        from_page: "批改結果頁面",
      });
      
      // 清除 viewMode 標記
      sessionStorage.removeItem("viewMode");
      navigate("/student-home");
    } else {
      // 正常的學生流程，再改一篇
      trackEvent('edit_another_click', {
        event_category: "navigation",
        event_label: "點擊再改一篇按鈕",
        from_page: "批改結果頁面",
      });
      
      // 從 workflowResult 或 sessionStorage 獲取 bookId
      const workflowResultString = sessionStorage.getItem("workflowResult");
      let bookId = '';
      let bookName = '';
      
      if (workflowResultString) {
        try {
          const workflowResult = JSON.parse(workflowResultString);
          bookId = workflowResult.bookID || '';
          bookName = workflowResult.bookID || ''; // 通常 bookID 就是書名
        } catch (error) {
          console.error('解析 workflowResult 失敗:', error);
        }
      }
      
      // 建構帶參數的 URL
      if (bookId) {
        navigate(`/upload-essay?bookId=${encodeURIComponent(bookId)}&bookName=${encodeURIComponent(bookName)}`);
      } else {
        // 如果沒有 bookId，回到學生首頁讓用戶重新選擇
        navigate("/student-home");
      }
    }
  };


  // 獲取評分標準 - 使用通用標準
  /* const getScoringRules = useCallback(() => {
    // 從 sessionStorage 獲取章節資料
    const topicDataString = sessionStorage.getItem("topicData");
    if (topicDataString) {
      try {
        const topicData = JSON.parse(topicDataString);
        // 如果章節資料包含評分標準，使用章節的標準
        if (topicData.scoringRules) {
          return topicData.scoringRules;
        }
      } catch (e) {
        console.error("解析章節資料失敗:", e);
      }
    }

    // 使用通用評分標準
    return [
      {
        grade: "A+",
        score: "25~22",
        criteria: "充分表達自己的體驗，敘寫生動，結構謄順，文前後呼應"
      },
      {
        grade: "A",
        score: "21~18", 
        criteria: "敘寫細膩，結構穩妥，文敘順暢者"
      },
      {
        grade: "B+",
        score: "17~14",
        criteria: "表達自己的體驗，敘寫具體，結構適當，文敘平順者"
      },
      {
        grade: "B",
        score: "13~10",
        criteria: "未就自己的經驗發揮，敘寫平實，文敘尚稱通順者"
      },
      {
        grade: "C+",
        score: "9~6",
        criteria: "敘寫浮泛，文敘不佳，或偏離焦點，文敘欠通順"
      },
      {
        grade: "C",
        score: "5~1",
        criteria: "無法掌握題旨，敘寫雜亂，文句不通，文敘抽象"
      },
      {
        grade: "0",
        score: "0",
        criteria: "空白卷，或文不對題、僅抄錄題幹"
      }
    ];
  }, [unitId]);
  */

  // 渲染評分標準表格 - 暫時註解掉
  /*
  const ScoreTable = useCallback(() => {
    const rules = getScoringRules();
    // 獲取評分說明
    let scoringNotes = "";
    const topicDataString = sessionStorage.getItem("topicData");
    if (topicDataString) {
      try {
        const topicData = JSON.parse(topicDataString);
        scoringNotes = topicData.scoringNotes || "";
      } catch (e) {
        console.error("解析章節評分說明失敗:", e);
      }
    }
    
    if (!rules || rules.length === 0) {
      // 如果找不到評分標準，顯示默認表格
      return (
        <div className="overflow-x-auto text-xs">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border text-left">作答情形</th>
                <th className="p-2 border w-12 text-center">等第</th>
                <th className="p-2 border w-12 text-center">分數</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border">
                  表現優異，達成課程學習目標，條理分明，敘寫生動
                </td>
                <td className="p-2 border text-center">A</td>
                <td className="p-2 border text-center">頂標</td>
              </tr>
              <tr>
                <td className="p-2 border">
                  表現良好，基本達成學習目標，內容適切，結構完整
                </td>
                <td className="p-2 border text-center">B</td>
                <td className="p-2 border text-center">均標</td>
              </tr>
              <tr>
                <td className="p-2 border">
                  表現不足，未能達成學習目標，內容簡略，結構欠佳
                </td>
                <td className="p-2 border text-center">C</td>
                <td className="p-2 border text-center">底標</td>
              </tr>
              <tr>
                <td className="p-2 border">空白卷，或文不對題、僅抄錄題幹</td>
                <td className="p-2 border text-center">0</td>
                <td className="p-2 border text-center">0</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-gray-500">
            請根據所選課次參考評分標準
          </p>
        </div>
      );
    }
    
    // 如果有評分標準，動態渲染表格
    return (
      <div className="overflow-x-auto text-xs">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border text-left">作答情形</th>
              <th className="p-2 border w-12 text-center">等第</th>
              <th className="p-2 border w-12 text-center">分數</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule: { criteria: string; score: number; grade: string }, index: number) => (
              <tr key={index}>
                <td className="p-2 border">{rule.criteria}</td>
                <td className="p-2 border text-center">{rule.grade}</td>
                <td className="p-2 border text-center">{rule.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {scoringNotes && (
          <p className="mt-2 text-xs text-gray-500">
            <strong>題目設計與配分說明：</strong> {scoringNotes}
          </p>
        )}
      </div>
    );
  }, [getScoringRules, unitId]);
  */


  // 從 URL 載入圖片並轉換為 base64
  const loadImageAsBase64 = (url: string): Promise<ImageData | null> => {
    return new Promise((resolve) => {
      // 方法1: 嘗試使用 crossOrigin
      const img = new Image();
      img.crossOrigin = 'anonymous';

      const tryWithoutCors = () => {
        // 方法2: 不用 crossOrigin，直接從已載入的圖片讀取
        console.log('嘗試不帶 CORS 載入圖片...');
        const img2 = new Image();

        img2.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img2.naturalWidth;
            canvas.height = img2.naturalHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              console.error('無法獲取 canvas context');
              resolve(null);
              return;
            }

            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img2, 0, 0);

            // 這裡可能會因為 tainted canvas 而失敗
            const base64 = canvas.toDataURL('image/jpeg', 0.9);
            console.log('圖片轉換成功（無 CORS）');

            resolve({
              base64: base64,
              mimeType: 'image/jpeg',
              width: img2.naturalWidth,
              height: img2.naturalHeight
            });
          } catch (error) {
            console.error('Canvas 轉換失敗（tainted canvas）:', error);
            resolve(null);
          }
        };

        img2.onerror = () => {
          console.error('圖片載入失敗（無 CORS）:', url);
          resolve(null);
        };

        img2.src = url;
      };

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.error('無法獲取 canvas context');
            tryWithoutCors();
            return;
          }

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          const base64 = canvas.toDataURL('image/jpeg', 0.9);
          console.log('圖片轉換成功（帶 CORS）, 尺寸:', img.naturalWidth, 'x', img.naturalHeight);

          resolve({
            base64: base64,
            mimeType: 'image/jpeg',
            width: img.naturalWidth,
            height: img.naturalHeight
          });
        } catch (error) {
          console.error('Canvas 轉換失敗，嘗試不帶 CORS:', error);
          tryWithoutCors();
        }
      };

      img.onerror = () => {
        console.error('圖片載入失敗（帶 CORS），嘗試不帶 CORS:', url);
        tryWithoutCors();
      };

      img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
    });
  };

  // 獲取學生作文圖片的 base64 數據
  const getStudentImages = async (): Promise<ImageData[]> => {
    const images: ImageData[] = [];

    try {
      const workflowResult = JSON.parse(sessionStorage.getItem("workflowResult") || "{}");
      const rawImageUrls = workflowResult.studentInputOriginUrl || workflowResult.studentInputUrl || "";
      const imageUrls = rawImageUrls
        ? rawImageUrls.split(",").map((url: string) => url.trim()).filter((url: string) => url)
        : [];

      for (const url of imageUrls) {
        const imageData = await loadImageAsBase64(url);
        if (imageData) {
          images.push(imageData);
        }
      }
    } catch (error) {
      console.error('獲取圖片失敗:', error);
    }

    return images;
  };

  // 處理 Word 下載
  const handleDownloadWord = async () => {
    // 追蹤下載點擊事件
    trackEvent('download_word_clicked', {
      event_category: "engagement",
      event_label: "下載Word",
      upload_mode: uploadMode,
      total_score: evaluationData?.totalScore || "未知",
    });

    // 準備學生資料
    const workflowResult = JSON.parse(sessionStorage.getItem("workflowResult") || "{}");
    const studentName = workflowResult.studentName || "";
    const studentSeatNo = workflowResult.studentSeatNo || "";
    const essayTitle = workflowResult.chapterID || "";
    const studentEssay = workflowResult.studentInput || "";

    // 從頁面獲取圖片 base64 數據（如果有的話）
    let studentImages: ImageData[] = [];
    console.log('uploadMode:', uploadMode);
    if (uploadMode === "image") {
      console.log('開始獲取圖片...');
      studentImages = await getStudentImages();
      console.log('獲取到的圖片數量:', studentImages.length);
    }

    // 如果是圖片模式但無法獲取圖片，顯示提示
    if (uploadMode === "image" && studentImages.length === 0) {
      console.warn('無法獲取圖片 base64 數據，可能是 CORS 限制');
    }

    // 呼叫下載功能
    await downloadCorrectionResult(
      {
        totalScore: evaluationData?.totalScore,
        categories: evaluationData?.categories?.map((cat) => ({
          name: cat.name,
          score: cat.score,
          comment: cat.comment,
        })),
        overallComment: evaluationData?.overallComment,
        suggestions: evaluationData?.suggestions,
        essayTitle: essayTitle,
        studentEssay: studentEssay,
        studentImages: studentImages,
      },
      {
        seatNumber: studentSeatNo,
        studentName: studentName,
      },
      () => {
        // onStart
        trackEvent('download_word_started', {});
      },
      () => {
        // onComplete
        trackEvent('download_word_completed', {});
      },
      (errorMessage) => {
        // onError
        alert(errorMessage);
        trackEvent('download_word_error', {
          error_message: errorMessage,
        });
      }
    );
  };

  // 處理PDF下載和列印（保留舊功能，暫時未使用）
  // @ts-expect-error 保留舊功能，暫時未使用
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePrintPDF = () => {
    // 追蹤PDF下載/列印點擊事件
    trackEvent('print_pdf_clicked', {
      event_category: "engagement",
      event_label: "另存PDF/列印",
      upload_mode: uploadMode,
      total_score: evaluationData?.totalScore || "未知",
    });

    // 創建新窗口用於列印/PDF下載
    createPrintWindow();
  };

  // 創建列印窗口的函數
  const createPrintWindow = () => {
    const printWindow = window.open("", "_blank", "width=1000,height=800");

    if (!printWindow) {
      alert("彈出窗口被阻擋，請允許彈出窗口後重試");
      trackEvent('print_window_blocked', {
        error_type: 'popup_blocked'
      });
      return;
    }

    // 準備數據
    const totalScore = String(evaluationData?.totalScore || "0");
    const maxScore = String(evaluationData?.maxPossibleScore || "25");
    const overallComment = evaluationData?.overallComment || "";
    const suggestions = evaluationData?.suggestions || [];
    const hasCategories = Boolean(
      evaluationData?.categories && evaluationData.categories.length > 0
    );
    const categories: PrintableCategory[] = (evaluationData?.categories || []).map((category) => ({
      ...category,
      displayLabel: renderCategoryLabel(category),
    }));
    const wordCount = evaluationData?.wordCount || "";
    
    // 準備學生作文數據
    const workflowResult = JSON.parse(sessionStorage.getItem("workflowResult") || "{}");
    const rawStudentInputUrl = workflowResult.studentInputOriginUrl || workflowResult.studentInputUrl || "";
    const studentInputUrls = rawStudentInputUrl ? rawStudentInputUrl.split(',').map((url: string) => url.trim()).filter(Boolean) : [];
    const studentInput = workflowResult.studentInput || "";
    const studentName = workflowResult.studentName || "未知";
    const className = workflowResult.className || workflowResult.bookID || "未知班級";
    const studentSeatNo = workflowResult.studentSeatNo || "0";
    const chapterName = workflowResult.chapterID || "未知題目";
    const markdown = workflowResult.markdown || "";

    // 寫入HTML內容
    printWindow.document.write(generatePrintHTML({
      totalScore,
      maxScore,
      overallComment,
      suggestions,
      hasCategories,
      categories,
      wordCount,
      studentInputUrls,
      studentInput,
      studentName,
      className,
      studentSeatNo,
      chapterName,
      markdown
    }));

    printWindow.document.close();
    
    trackEvent('print_window_created', {
      has_categories: hasCategories,
      has_suggestions: suggestions.length > 0
    });
  };

  // 生成列印HTML的函數
  const generatePrintHTML = (data: {
    totalScore: string;
    maxScore: string;
    overallComment: string;
    suggestions: string[];
    hasCategories: boolean;
    categories: PrintableCategory[];
    wordCount: string;
    studentInputUrls: string[];
    studentInput: string;
    studentName: string;
    className: string;
    studentSeatNo: string;
    chapterName: string;
    markdown: string;
  }) => {
    const {
      totalScore,
      maxScore,
      overallComment,
      suggestions,
      hasCategories,
      categories,
      wordCount,
      studentInputUrls,
      studentInput,
      studentName,
      className,
      studentSeatNo,
      chapterName,
      markdown
    } = data;

    return `
      <!DOCTYPE html>
      <html lang="zh-TW">
      <head>
        <meta charset="UTF-8">
        <title>作文批改結果</title>
        <style>
          /* 基本樣式 */
          body, html {
            margin: 0;
            padding: 0;
            font-family: "微軟正黑體", Arial, sans-serif;
            background-color: white !important;
            color: #333;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .container {
            max-width: 1000px;
            margin: 20px auto;
            background: white !important;
            padding: 20px;
            border-radius: 8px;
          }
          
          .header {
            text-align: center;
            padding-bottom: 20px;
            margin-bottom: 20px;
            border-bottom: 1px solid #eee;
          }
          
          .header h1 {
            color: #254D71 !important;
            font-size: 24px;
            margin-bottom: 8px;
          }
          
          .header p {
            color: #666 !important;
            font-size: 14px;
            margin: 0;
          }
          
          .score-card {
            background-color: #27E0DB !important;
            color: white !important;
            border-radius: 0 15px 15px 15px;
            text-align: center;
            margin-bottom: 20px;
            height: 200px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-self: start;
          }
          
          .score-card .score {
            font-size: 72px;
            font-weight: bold;
            line-height: 1;
            color: white !important;
          }
          
          .category-card {
            background: white !important;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
            border: 1px solid #eee;
          }
          
          .category-title {
            color: #046CB7 !important;
            font-weight: bold;
            margin-bottom: 10px;
            font-size: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .content {
            font-size: 14px;
            line-height: 1.5;
            color: #333 !important;
          }
          
          .grid {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 20px;
          }
          
          .essay-grid {
            display: grid;
            grid-template-columns: 2fr 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
            align-items: start;
          }
          
          .essay-content,
          .score-container, 
          .student-info-card {
            background: white !important;
            border-radius: 8px;
            padding: 15px;
            min-height: 200px;
          }
          
          .essay-grid h3 {
            color: #046CB7 !important;
            font-weight: bold !important;
            margin-bottom: 15px !important;
            margin-top: 0 !important;
          }
          
          .essay-image {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            max-height: 300px;
          }
          
          .essay-image-multiple {
            width: 100%;
            height: 180px;
            object-fit: contain;
            border-radius: 8px;
            background-color: #f9f9f9;
          }
          
          .info-item {
            margin-bottom: 10px;
          }
          
          .info-label {
            font-size: 12px;
            color: #666 !important;
            margin-bottom: 3px;
          }
          
          .info-value {
            font-weight: bold;
            color: #333 !important;
          }
          
          .comment-card {
            background-color: #f0f9fa !important;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
          }
          
          .comment-title {
            color: #046CB7 !important;
            font-weight: bold;
            margin-bottom: 10px;
            font-size: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          ul.suggestions {
            margin-left: 20px;
            padding-left: 0;
          }
          
          ul.suggestions li {
            margin-bottom: 5px;
            color: #333 !important;
          }
          
          .footer {
            text-align: center;
            margin-top: 30px;
            color: #666 !important;
            font-size: 12px;
            width: 100%;
          }
          
          .footer p {
            margin: 0;
            text-align: center;
          }
          
          .buttons {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 20px;
          }
          
          .buttons button {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
          }
          
          .buttons button.print {
            background-color: #254D71 !important;
            color: white !important;
          }
          
          .buttons button.close {
            background-color: #e5e7eb !important;
            color: #4b5563 !important;
          }
          
          @media print {
            .buttons {
              display: none !important;
            }
            
            body {
              background: white !important;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="container" id="capture-container">
          <div class="header">
            <h1>翰林AI寫作批改結果</h1>
            <p>題目：${escapeHtml(chapterName)}</p>
            <p>產生時間: ${new Date().toLocaleString()}</p>
          </div>
          
          <!-- 學生作文和基本資訊區塊 -->
          <div class="essay-grid">
            <div class="essay-content">
              <h3>學生作文</h3>
              ${studentInputUrls.length > 0 ? `
                <div style="text-align: center;">
                  ${studentInputUrls.length === 1 ? 
                    `<img src="${escapeHtml(studentInputUrls[0])}" alt="學生上傳的作文圖片" class="essay-image" />` :
                    `<div style="display: flex; flex-direction: column; gap: 10px; align-items: center;">
                       <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; max-width: 800px;">
                         ${studentInputUrls.map((url: string, index: number) => 
                           `<img src="${escapeHtml(url)}" alt="學生上傳的作文圖片 ${index + 1}" class="essay-image-multiple" style="border: 1px solid #ddd;" />`
                         ).join('')}
                       </div>
                     </div>`
                  }
                </div>
              ` : `
                <div style="font-size: 14px; line-height: 1.6; color: #333;">
                  ${escapeHtml(studentInput) || '無文章內容'}
                </div>
              `}
            </div>
            
            <div class="score-container">
              <h3>總分</h3>
              <div class="score-card">
                <p style="margin: 0; font-size: 16px; color: white;">總分 SCORE</p>
                <div class="score">${totalScore}</div>
                <p style="margin: 0; font-size: 20px; color: white;">/ ${maxScore}</p>
              </div>
            </div>
            
            <div class="student-info-card">
              <h3>學生資訊</h3>
              <div class="info-item">
                <div class="info-label">姓名</div>
                <div class="info-value">${escapeHtml(studentName)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">班級</div>
                <div class="info-value">${escapeHtml(className)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">座號</div>
                <div class="info-value">${escapeHtml(studentSeatNo)}</div>
              </div>
            </div>
          </div>
          
          <div class="grid">
            <div class="left-column">
              ${wordCount ? `
                <div class="category-card">
                  <div class="category-title">字數檢查</div>
                  <div class="content">${escapeHtml(wordCount)}</div>
                </div>
              ` : ''}
              
              ${hasCategories ? categories
                .filter((category: PrintableCategory) => category.name !== "整體評分")
                .map((category: PrintableCategory) => `
                  <div class="category-card">
                    <div class="category-title">
                      ${escapeHtml(category.name)}－－${
                        category.name === "題旨發揮" ? "能掌握題幹要求，緊扣題旨發揮" :
                        category.name === "資料掌握" ? "能融會貫通題幹資料，舉證詳實" :
                        category.name === "結構安排" ? "結構嚴謹、前後貫通、脈絡清楚" :
                        category.name === "字句應用" ? "標點符號、字句妥切，邏輯清晰" :
                        category.name === "立意取材" ? "主旨明確緊扣題意，取材具體深刻且有層次。" :
                        category.name === "結構組織" ? "結構完整層次分明，起承轉合流暢且呼應。" :
                        category.name === "遣詞造句" ? "用詞精確句型多變，文句通順流暢具節奏。" :
                        category.name === "錯別字、格式與標點符號" ? "字句標點錯誤極少，格式正確且閱讀流暢。" : ""
                      }${category.displayLabel ? `：${escapeHtml(category.displayLabel)}` : ""}
                    </div>
                    <div class="content">${escapeHtml(category.comment)}</div>
                  </div>
                `).join('') : ''}
            </div>
            
            <div class="right-column">
              ${overallComment ? `
                <div class="comment-card">
                  <div class="comment-title">
                    整體評語
                  </div>
                  <div class="content">${escapeHtml(overallComment)}</div>
                </div>
              ` : ''}
              
              ${suggestions && suggestions.length > 0 ? `
                <div class="comment-card">
                  <div class="comment-title">寫作建議</div>
                  <ul class="suggestions">
                    ${suggestions.map((suggestion: string) => `<li>${escapeHtml(suggestion)}</li>`).join('')}
                  </ul>
                </div>
              ` : ''}
            </div>
          </div>
          
          <!-- 評分標準表格 - 動態生成 -->
          <div id="scoring-table-container" style="margin-top: 30px; margin-bottom: 20px;">
            <!-- 將由 JavaScript 動態生成評分表格 -->
          </div>
          
          <div class="footer">
            <p>Copyright © 2024 Hanlin 翰林出版事業股份有限公司</p>
          </div>
        </div>
        
        <div class="buttons">
          <button class="print" onclick="printResult()">下載Word</button>
          <button class="close" onclick="window.close()">關閉</button>
        </div>
        
        <script>
          // 解析 Markdown 表格並生成 HTML
          function parseMarkdownTable(markdownText) {
            if (!markdownText || markdownText.trim() === '') {
              return null;
            }

            const lines = markdownText.trim().split('\\n');
            if (lines.length < 3) return null;

            // 提取表頭
            const headerLine = lines[0];
            const headers = headerLine.split('|').map(function(cell) { return cell.trim(); }).filter(function(cell) { return cell !== ''; });

            // 跳過分隔線，提取數據行
            const dataLines = lines.slice(2);
            const rows = dataLines.map(function(line) {
              return line.split('|').map(function(cell) { return cell.trim(); }).filter(function(cell) { return cell !== ''; });
            }).filter(function(row) { return row.length > 0; });

            return { headers: headers, rows: rows };
          }

          function generateTableHTML(tableData) {
            if (!tableData) {
              return '<p style="color: #999; font-size: 12px;">無評分標準表格</p>';
            }

            var html = '<table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd; background: white !important;">';

            // 生成表頭
            html += '<thead style="background-color: #f5f5f5 !important;"><tr>';
            for (var i = 0; i < tableData.headers.length; i++) {
              html += '<th style="border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #666 !important;">' + tableData.headers[i] + '</th>';
            }
            html += '</tr></thead>';

            // 生成表體
            html += '<tbody>';
            for (var i = 0; i < tableData.rows.length; i++) {
              var bgColor = i % 2 === 0 ? 'white' : '#f9f9f9';
              html += '<tr style="background-color: ' + bgColor + ' !important;">';
              for (var j = 0; j < tableData.rows[i].length; j++) {
                html += '<td style="border: 1px solid #ddd; padding: 8px 12px; font-size: 12px; color: #666 !important;">' + tableData.rows[i][j] + '</td>';
              }
              html += '</tr>';
            }
            html += '</tbody>';

            html += '</table>';
            return html;
          }

          function printResult() {
            if (window.opener && window.opener.gtag) {
              window.opener.gtag('event', 'print_pdf_executed', {
                event_category: 'result',
                event_label: '另存PDF/列印執行'
              });
            }

            window.print();
          }

          window.onload = function() {
            // 生成評分標準表格
            var markdown = ${JSON.stringify(markdown).replace(/</g, "\\u003c")};
            var tableData = parseMarkdownTable(markdown);
            var tableHTML = generateTableHTML(tableData);
            document.getElementById('scoring-table-container').innerHTML = tableHTML;

            document.querySelector('.print').focus();

            if (window.opener && window.opener.gtag) {
              window.opener.gtag('event', 'print_window_loaded', {
                event_category: 'result'
              });
            }
          };
        </script>
      </body>
      </html>
    `;
  };


  // 更新下載狀態 - 暫時註解掉，因為 API 端點不存在
  /*
  const updateDownloadStatusHandler = async () => {
    try {
      // 獲取記錄 ID
      const recordId = sessionStorage.getItem("correctionRecordId");

      // 如果沒有記錄 ID，則無法更新
      if (!recordId) {
        console.error("無法更新下載狀態：找不到記錄 ID");
        return;
      }

      // 使用 API 工具更新下載狀態
      const result = await updateDownloadStatus(recordId);

      if (result.success) {

        // 追蹤下載狀態更新成功
        trackEvent("download_status_updated", {
          event_category: "api",
          event_label: "下載狀態已更新",
          environment: getCurrentEnvironment(),
          record_id: recordId,
        });
      } else {
        console.error("更新下載狀態失敗:", result.error);

        // 追蹤更新失敗
        trackEvent("download_status_update_error", {
          event_category: "api",
          event_label: "下載狀態更新失敗",
          error_message: result.error?.message || "未知錯誤",
        });
      }
    } catch (error: unknown) {
      console.error("更新下載狀態時出錯:", error);

      // 追蹤更新異常
      trackEvent("download_status_update_exception", {
        event_category: "api",
        event_label: "下載狀態更新異常",
        error_message: error.message,
      });
    }
  };
  */

  // 檢查是否有分類評分數據
  const hasCategories =
    evaluationData?.categories && evaluationData.categories.length > 0;
  // 檢查是否有錯誤訊息
  const hasError =
    evaluationData && evaluationData.error && evaluationData.error !== null;

  // 老師檢視模式的就地編輯資訊(非老師檢視/舊格式為 null → 全頁零編輯 UI、不出現「修改評語」)
  const teacherEditCtx = getTeacherEditContext();

  // 修訂模式進行中(草稿已初始化):渲染層以此決定欄位顯示讀取畫面或虛線編輯框
  const revising = revisionMode && revisionDraft !== null && teacherEditCtx !== null;

  // 建議清單單列:修訂模式變成虛線 textarea＋刪除鈕(整批草稿內操作，儲存才送出)
  const renderSuggestionRow = (index: number, readNode: ReactNode) => {
    if (!revising || !revisionDraft) return readNode;
    return (
      <RevisionFieldWrap fieldId={`sug:${index}`}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <RevisionTextarea
              value={revisionDraft.suggestions[index] ?? ""}
              onChange={(value) =>
                updateRevisionDraft((draft) => ({
                  ...draft,
                  suggestions: draft.suggestions.map((s, i) => (i === index ? value : s)),
                }))
              }
              ariaLabel={`建議 ${index + 1}`}
              rows={2}
              placeholder={`建議 ${index + 1}`}
              error={revisionErrors[`sug:${index}`]}
              disabled={revisionSaving}
            />
          </div>
          <button
            type="button"
            onClick={() =>
              // 刪除前先跳站內確認彈窗，按「確認」才真的從草稿移除
              setRevisionPopup({
                message: `確定要刪除建議 ${index + 1} 嗎？`,
                onConfirm: () =>
                  updateRevisionDraft((draft) => ({
                    ...draft,
                    suggestions: draft.suggestions.filter((_, i) => i !== index),
                  })),
              })
            }
            disabled={revisionSaving}
            aria-label={`刪除建議 ${index + 1}`}
            className="shrink-0 px-1 text-xl leading-none text-gray-400 transition-colors hover:text-red-500"
          >
            ×
          </button>
        </div>
      </RevisionFieldWrap>
    );
  };

  // 建議清單尾端:空清單提示＋修訂模式的「＋新增建議」(在草稿追加一列)
  const renderSuggestionExtras = (suggestionCount: number) => {
    if (!teacherEditCtx) return null;
    return (
      <>
        {suggestionCount === 0 && (
          <p className="text-sm text-gray-400">目前沒有寫作建議</p>
        )}
        {revising && (
          <button
            type="button"
            onClick={() =>
              updateRevisionDraft((draft) => ({
                ...draft,
                suggestions: [...draft.suggestions, ""],
              }))
            }
            disabled={revisionSaving}
            className="mt-2 text-sm text-[#254D71] border border-[#254D71] rounded-md px-3 py-1 hover:bg-gray-50"
          >
            ＋新增建議
          </button>
        )}
      </>
    );
  };

  // 建議清單資料來源:修訂模式吃草稿(可增刪)、檢視模式吃解析結果
  const suggestionList = revising && revisionDraft
    ? revisionDraft.suggestions
    : evaluationData?.suggestions || [];

  // 整體評語內容:修訂模式變成虛線 textarea、檢視模式維持原樣(有兩處版位共用)
  const renderOverallComment = () => {
    if (revising && revisionDraft) {
      return (
        <RevisionFieldWrap fieldId="overall">
          <RevisionTextarea
            value={revisionDraft.overall}
            onChange={(value) =>
              updateRevisionDraft((draft) => ({ ...draft, overall: value }))
            }
            ariaLabel="整體評語"
            rows={4}
            placeholder="整體評語"
            error={revisionErrors["overall"]}
            disabled={revisionSaving}
          />
        </RevisionFieldWrap>
      );
    }
    return (
      <p className="text-gray-700 text-sm">
        {evaluationData?.overallComment || (
          <span className="text-gray-400">（目前沒有整體評語）</span>
        )}
      </p>
    );
  };

  // 未登入或非老師身份狀態顯示
  if (!isAuthenticated && !loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            尚未登入或權限不足
          </h1>
          <p className="text-gray-700 mb-4">即將回到首頁...</p>
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-blue-800 mb-4">
            資料載入中...
          </h1>
          <div className="w-12 h-12 border-4 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // 如果有錯誤訊息，顯示錯誤頁面
  if (hasError) {
    return (
      <>
        <title>批改結果 - 翰林AI寫作批改</title>

        <div className="flex flex-col min-h-screen bg-gray-50">
          {/* 頂部導航欄 */}
          <header className="bg-[#254D71] text-white py-3">
            <div className="container mx-auto px-2 flex justify-between items-center">
              {/* 左側 Logo - 添加點擊事件 */}
              <div className="flex items-center">
                <img
                  src={customLogo}
                  alt="翰林AI寫作批改"
                  className="h-10 cursor-pointer"
                  onClick={handleLogoClick}
                />
              </div>
            </div>
          </header>

          {/* 主要內容區域 - 錯誤提示 */}
          <main className="flex-grow container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                {sessionStorage.getItem("viewMode") === "teacherRecord" ? (
                  <BreadCrumb breadCrumb="/teacher/correcting-records/result" />
                ) : sessionStorage.getItem("viewMode") === "studentRecord" ? (
                  <BreadCrumb breadCrumb="/student/record/result" />
                ) : (
                  <BreadCrumb breadCrumb="/student/upload/result" />
                )}
              </div>
            </div>

            {/* 錯誤提示 */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 mt-4 text-center">
              <div className="text-red-600 text-5xl mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-16 h-16 mx-auto"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
              </div>
              <h2 className="font-bold text-xl mb-4">
                AI無法辨識，請重新拍照或掃描
              </h2>
              <p className="text-gray-700 mb-3">{evaluationData?.error}</p>
              <button
                onClick={handleBreadcrumbClick}
                className="px-6 py-3 text-white font-medium bg-[#254D71] rounded-md inline-block"
              >
                重新上傳作文
              </button>
            </div>
          </main>

          {/* 頁腳 */}
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <title>批改結果 - 翰林AI寫作批改</title>

      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* 頂部導航欄 */}
        <Header />

        {/* 主要內容區域 */}
        <main className="flex-grow container mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-600">
              {sessionStorage.getItem("viewMode") === "teacherRecord" ? (
                <BreadCrumb breadCrumb="/teacher/correcting-records/result" />
              ) : sessionStorage.getItem("viewMode") === "studentRecord" ? (
                <BreadCrumb breadCrumb="/student/record/result" />
              ) : (
                <BreadCrumb breadCrumb="/student/upload/result" />
              )}
            </div>

            <div className="flex items-center flex-wrap gap-2 md:gap-3">
              {/* 上一位/下一位、修改評語(或取消/儲存)、返回、下載Word同排,375px 寬手機版容易超版,
                  故容器改 flex-wrap + gap(取代 space-x-3),擠不下時自動換行,
                  不會橫向溢出捲動。順序與尺寸比照國中版(kanban 106957 後續修正)。 */}
              {/* 老師修訂模式:檢視時「修改評語」鈕，修訂中換成「取消／儲存」，與其他工具列按鈕同排
                  (僅老師檢視記錄且結果可編輯時出現;學生檢視/舊格式紀錄零變化) */}
              {teacherEditCtx &&
                (revising ? (
                  <RevisionActionBar
                    onCancel={cancelRevision}
                    onSave={saveRevision}
                    saving={revisionSaving}
                    saveDisabled={hasRevisionErrors}
                  />
                ) : (
                  <RevisionEditButton onClick={enterRevision} />
                ))}
              {/* 上一位 / N-M / 下一位:同章節同篩選的學生切換(kanban 106957 R5)。
                  只在有 2 筆以上清單時顯示(單筆或無清單不出現);樣式沿用返回/下載那排的
                  站內主色 #254D71,inline 邊框群組。修訂中切換由 guardedGoToSibling 先確認。 */}
              {shouldShowSwitcher(feedbackSiblings) && (
                <div className="inline-flex items-center rounded-md border border-[#254D71]/30 bg-white overflow-hidden shadow-sm">
                  <button
                    onClick={() => guardedGoToSibling(-1)}
                    disabled={!canGoPrev(feedbackSiblings, siblingIndex) || revisionSaving}
                    className="flex items-center gap-1 px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base font-medium text-[#254D71] hover:bg-[#254D71]/5 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                    aria-label="上一位學生"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    上一位
                  </button>
                  <span className="px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm font-semibold text-[#254D71] bg-[#254D71]/5 border-x border-[#254D71]/20 tabular-nums">
                    {siblingIndex + 1} / {feedbackSiblings.length}
                  </span>
                  <button
                    onClick={() => guardedGoToSibling(1)}
                    disabled={!canGoNext(feedbackSiblings, siblingIndex) || revisionSaving}
                    className="flex items-center gap-1 px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base font-medium text-[#254D71] hover:bg-[#254D71]/5 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                    aria-label="下一位學生"
                  >
                    下一位
                    <svg className="w-4 h-4 md:w-5 md:h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
              {/* 重新體驗按鈕或返回按鈕 */}
              <button
                onClick={handleRetryClick}
                className="px-3 py-1 md:py-2.5 md:px-4 md:text-lg text-white font-medium bg-[#254D71] rounded-md min-w-[120px] min-h-[30px] md:min-w-[140px] md:min-h-12"
              >
                {sessionStorage.getItem("viewMode") === "teacherRecord" ? "返回" : sessionStorage.getItem("viewMode") === "studentRecord" ? "返回" : "再改一篇"}
              </button>
              {/* Word下載按鈕:原在下方「作文題目」那排,搬到工具列同排、尺寸比照國中版(kanban 106957 後續修正) */}
              <button
                onClick={handleDownloadWord}
                className="px-3 py-1 md:py-2.5 md:px-4 md:text-lg text-white font-medium bg-[#254D71] rounded-md min-w-[120px] min-h-[30px] md:min-w-[140px] md:min-h-12"
              >
                下載Word
              </button>
            </div>
          </div>

          {/* 題目區塊 - 移到批改結果上方 */}
          <div className="mb-6">
            <div className="flex items-center">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">作文題目：</span>
                <button
                onClick={async () => {
                  try {
                    const workflowResultString = sessionStorage.getItem("workflowResult");
                    if (workflowResultString) {
                      const workflowResult = JSON.parse(workflowResultString);
                      const chapterName = workflowResult.chapterID;
                      
                      // 獲取書籍ID
                      const bookId = workflowResult.bookID || sessionStorage.getItem("selectedBookId");
                      const userData = JSON.parse(localStorage.getItem('oidc.user:https://id.hle.com.tw:js') || '{}');
                      const sub = userData.profile?.sub || userData.profile?.['custom:id'];
                      
                      if (bookId && sub && chapterName) {
                        const chaptersRes = await getChapters(bookId, sub);
                        const chapters = chaptersRes.data || [];
                        const chapter = chapters.find((ch: Chapter) => ch.chapter === chapterName);
                        
                        if (chapter) {
                          setCurrentChapter(chapter);
                          setShowQuestionPopup(true);
                        } else {
                          alert("找不到題目詳情");
                        }
                      } else {
                        alert("缺少必要的題目資訊");
                      }
                    } else {
                      alert("找不到作業資訊");
                    }
                  } catch (error) {
                    console.error("獲取題目詳情失敗:", error);
                    alert("獲取題目詳情時發生錯誤");
                  }
                }}
                className="text-primary-light md:text-lg font-medium underline"
              >
                {(() => {
                  try {
                    const workflowResultString = sessionStorage.getItem("workflowResult");
                    if (workflowResultString) {
                      const workflowResult = JSON.parse(workflowResultString);
                      return workflowResult.chapterID || "未知題目";
                    }
                  } catch (error) {
                    console.error("獲取題目失敗:", error);
                  }
                  return "未知題目";
                })()}
                </button>
              </div>
            </div>
          </div>

          {/* QA Debug 面板：?debug=ocr 才顯示。給雅平驗證 OCR 分段是否正確用。 */}
          {debugOCR && (() => {
            try {
              const wf = JSON.parse(sessionStorage.getItem("workflowResult") || "{}");
              const sliceUrls: string[] = (wf.studentInputUrl || "")
                .split(",").map((u: string) => u.trim()).filter(Boolean);
              const originUrls: string[] = (wf.studentInputOriginUrl || "")
                .split(",").map((u: string) => u.trim()).filter(Boolean);
              const text: string = wf.studentInput || "";
              const paragraphs = text.split("\n\n").map(p => p.trim()).filter(Boolean);
              const charCount = text.length;
              const nlnlCount = (text.match(/\n\n/g) || []).length;
              const isCountMatch = sliceUrls.length === paragraphs.length;

              const Stat = ({ label, value, warn = false }: { label: string; value: number | string; warn?: boolean }) => (
                <div className={`p-2 rounded border ${warn ? 'bg-orange-50 border-orange-300' : 'bg-white border-yellow-300'}`}>
                  <div className="text-xs text-gray-600">{label}</div>
                  <div className={`text-xl font-bold ${warn ? 'text-orange-700' : 'text-gray-900'}`}>{value}</div>
                </div>
              );

              return (
                <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">🧪</span>
                    <h2 className="text-lg font-bold text-yellow-900">OCR Debug 面板</h2>
                    <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">QA 限定 ?debug=ocr</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <Stat label="切片數" value={sliceUrls.length} />
                    <Stat label="OCR 字元數" value={charCount} />
                    <Stat label="段落分隔符數 (\n\n)" value={nlnlCount} />
                    <Stat label="實際段落數" value={paragraphs.length} warn={!isCountMatch} />
                  </div>

                  {!isCountMatch && (
                    <div className="bg-orange-100 border border-orange-300 rounded p-2 mb-3 text-sm text-orange-900">
                      ⚠️ 切片數（{sliceUrls.length}）≠ 段落數（{paragraphs.length}）：可能某張圖 OCR 失敗或為空白被自動過濾。
                    </div>
                  )}

                  <details className="mb-2 bg-white rounded border border-yellow-300">
                    <summary className="cursor-pointer text-sm font-semibold text-yellow-900 p-2 hover:bg-yellow-100">
                      📋 各段字數列表（共 {paragraphs.length} 段）
                    </summary>
                    <div className="p-2 text-sm border-t border-yellow-200">
                      {paragraphs.length === 0 ? (
                        <div className="text-gray-500 italic">（無段落）</div>
                      ) : paragraphs.map((p, i) => (
                        <div key={i} className="py-1 border-b last:border-0 border-gray-100">
                          <span className="font-mono text-yellow-700">[段 {i + 1}]</span>
                          <span className="ml-2 text-gray-600 inline-block w-16">{p.length} 字</span>
                          <span className="ml-2 text-gray-700">{p.substring(0, 80)}{p.length > 80 ? "…" : ""}</span>
                        </div>
                      ))}
                    </div>
                  </details>

                  <details className="mb-2 bg-white rounded border border-yellow-300">
                    <summary className="cursor-pointer text-sm font-semibold text-yellow-900 p-2 hover:bg-yellow-100">
                      🖼️ 切片 ↔ 段落對應（共 {sliceUrls.length} 張切片）
                    </summary>
                    <div className="p-2 grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-yellow-200">
                      {sliceUrls.map((url, i) => (
                        <div key={i} className="border border-gray-200 rounded p-2 bg-gray-50">
                          <div className="flex gap-2">
                            <img
                              src={url}
                              alt={`切片 ${i + 1}`}
                              className="w-20 h-auto object-contain border border-gray-300 bg-white cursor-pointer"
                              onClick={() => { setPopupImageUrl(url); setShowImagePopup(true); }}
                            />
                            <div className="flex-1 text-xs">
                              <div className="font-bold mb-1">切片 {i + 1}</div>
                              {paragraphs[i] ? (
                                <div className="text-gray-700 leading-relaxed">
                                  <span className="text-gray-500">→ 段 {i + 1}（{paragraphs[i].length} 字）：</span><br />
                                  {paragraphs[i].substring(0, 100)}{paragraphs[i].length > 100 ? "…" : ""}
                                </div>
                              ) : (
                                <div className="text-red-600 italic">⚠️ 此切片無對應段落（可能空白被過濾）</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>

                  {originUrls.length > 0 && (
                    <details className="mb-2 bg-white rounded border border-yellow-300">
                      <summary className="cursor-pointer text-sm font-semibold text-yellow-900 p-2 hover:bg-yellow-100">
                        📷 校正後原圖（共 {originUrls.length} 張，未切割）
                      </summary>
                      <div className="p-2 grid grid-cols-2 md:grid-cols-3 gap-2 border-t border-yellow-200">
                        {originUrls.map((url, i) => (
                          <img
                            key={i}
                            src={url}
                            alt={`原圖 ${i + 1}`}
                            className="w-full h-auto object-contain border border-gray-300 bg-white cursor-pointer"
                            onClick={() => { setPopupImageUrl(url); setShowImagePopup(true); }}
                          />
                        ))}
                      </div>
                    </details>
                  )}

                  <details className="bg-white rounded border border-yellow-300">
                    <summary className="cursor-pointer text-sm font-semibold text-yellow-900 p-2 hover:bg-yellow-100">
                      📝 OCR 合併全文（{charCount} 字）
                    </summary>
                    <pre className="p-3 text-sm whitespace-pre-wrap font-sans text-gray-800 border-t border-yellow-200 max-h-96 overflow-auto">
{text || "（空）"}
                    </pre>
                  </details>
                </div>
              );
            } catch (e) {
              return (
                <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-700 mb-6">
                  Debug panel 載入失敗：{String(e)}
                </div>
              );
            }
          })()}

          {/* 批改結果 */}
          <div className="mb-6">
            <div className="mb-6">
              <h2 className="font-bold text-lg">批改結果</h2>
            </div>

            {/* 題目、學生作文、總分和學生資訊三區塊顯示 */}
            <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 mb-6">
              <div className="grid grid-cols-1 lg:grid-cols-8 gap-4 sm:gap-6">
                {/* 左側：學生作文 (佔4格) */}
                <div className="lg:col-span-4">
                  {/* 學生作文區塊 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">學生作文</h3>
                    {(() => {
                      try {
                        const workflowResultString = sessionStorage.getItem("workflowResult");
                        if (workflowResultString) {
                          const workflowResult = JSON.parse(workflowResultString);
                          
                          // 解析圖片 URL - 支援單張或多張（逗號分隔）
                          const rawImageUrls = workflowResult.studentInputOriginUrl || workflowResult.studentInputUrl || "";
                          const imageUrls = rawImageUrls ? rawImageUrls.split(',').map((url: string) => url.trim()).filter(Boolean) : [];
                          
                          
                          // 如果有圖片 URL，顯示圖片（支援多張）
                          if (imageUrls.length > 0) {
                            if (imageUrls.length === 1) {
                              // 單張圖片 - 原來的顯示方式
                              return (
                                <div className="rounded-lg min-h-60">
                                  <div className="flex items-center justify-center">
                                    <img
                                      src={imageUrls[0]}
                                      alt="學生上傳的作文圖片"
                                      className="max-w-full h-auto rounded cursor-pointer hover:shadow-md transition-shadow"
                                      onClick={() => {
                                        setPopupImageUrl(imageUrls[0]);
                                        setShowImagePopup(true);
                                      }}
                                      style={{ maxHeight: '400px' }}
                                    />
                                  </div>
                                </div>
                              );
                            } else {
                              // 多張圖片 - 並排顯示
                              return (
                                <div className="rounded-lg min-h-60">
                                  <div className="flex flex-col gap-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {imageUrls.map((url: string, index: number) => (
                                        <div key={index} className="flex justify-center">
                                          <img
                                            src={url}
                                            alt={`學生上傳的作文圖片 ${index + 1}`}
                                            className="max-w-full h-auto rounded cursor-pointer hover:shadow-md transition-shadow border border-gray-200"
                                            onClick={() => {
                                              setPopupImageUrl(url);
                                              setShowImagePopup(true);
                                            }}
                                            style={{ maxHeight: '300px' }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          }
                          
                          // 沒有圖片則顯示文字內容（固定高度）
                          return (
                            <div className="rounded-lg p-4 overflow-y-auto">
                              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                                {workflowResult.studentInput || "無文章內容"}
                              </p>
                            </div>
                          );
                        }
                      } catch (error) {
                        console.error("獲取學生作文失敗:", error);
                      }
                      return (
                        <div className="rounded-lg p-4 h-40">
                          <p className="text-gray-700 text-sm leading-relaxed">
                            無文章內容
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* 中間和右側：兩個完全相同大小的區塊 */}
                <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 總分區塊 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">總分</h3>
                    <div className="bg-[#27E0DB] rounded-tr-4xl rounded-bl-4xl rounded-br-4xl p-8 text-center text-white">
                      <p className="text-xl mb-2 opacity-90">總分 SCORE</p>
                      {revising && revisionDraft && canAutoSumTotal ? (
                        // 可自動加總:總分改成唯讀大字，值由各構面得分帶動(kanban 106732)
                        // tabular-nums 是必要的——等寬數字才不會在 17→18.5→9 之間左右位移，
                        // 位移疊上縮放會看起來像在抖;leading-none mb-2 與檢視模式一致，切模式時大字不上下跳
                        <RevisionFieldWrap fieldId="total">
                          {/* 標籤放在 live region 外面:掛 aria-label 在 live region 上，
                              讀屏每次更新只會念標籤、念不到新數字，正好與這段設計意圖相反(kanban 106732 審碼) */}
                          <span className="sr-only">總分（各項得分自動加總）</span>
                          <div
                            role="status"
                            aria-live="polite"
                            data-testid="revision-total-auto"
                            className={`text-6xl font-bold leading-none mb-2 tabular-nums inline-block origin-center transition-transform duration-200 ease-out motion-reduce:transition-none motion-reduce:transform-none ${
                              totalBump ? "scale-110" : "scale-100"
                            }${derivedTotal === null ? " text-white/70" : ""}`}
                          >
                            {derivedTotalDisplay}
                          </div>
                        </RevisionFieldWrap>
                      ) : revising && revisionDraft ? (
                        // 不可自動加總(六級分制/滿分對不上的變體):總分維持白底虛線輸入框、手動填
                        <RevisionFieldWrap fieldId="total">
                          <div className="mx-auto mb-2 max-w-[240px] rounded-xl bg-white p-3 text-left text-gray-800">
                            <RevisionScoreInput
                              value={revisionDraft.total}
                              onChange={(value) =>
                                updateRevisionDraft((draft) => ({ ...draft, total: value }))
                              }
                              ariaLabel="總分"
                              max={evaluationData?.maxPossibleScore || 25}
                              error={revisionErrors["total"]}
                              disabled={revisionSaving}
                            />
                          </div>
                        </RevisionFieldWrap>
                      ) : (
                        <div className="text-6xl font-bold leading-none mb-2">
                          {evaluationData?.totalScore || "0"}
                        </div>
                      )}
                      <p className="text-2xl opacity-90">
                        / {evaluationData?.maxPossibleScore || 25}
                      </p>
                      {/* 修訂模式說明列:自動加總那行(鎖頭＋加總算式)經 kenzy 指示移除，
                          總分區塊不掛任何說明文字。手動填的紀錄仍需要一句話交代「這裡可以自己打」，
                          否則老師會以為自己踩到 bug;文字用實白＋text-sm＋半透明深底壓對比
                          (12px 白字疊在 #27E0DB 上只有約 1.5:1)(kanban 106732) */}
                      {revising && revisionDraft && !canAutoSumTotal && (
                        <div className="mt-4 border-t border-white/30 pt-3">
                          <div
                            data-testid="revision-total-manual-hint"
                            className="inline-flex items-start justify-center gap-1.5 rounded-md bg-black/10 px-3 py-1.5 text-sm leading-relaxed text-white"
                          >
                            <PencilIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>這份可自行填寫總分</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 學生資訊 */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">學生資訊</h3>
                    <div className="bg-white rounded-lg">
                    <div className="space-y-3">
                      {/* 學生姓名 */}
                      <div className="rounded-lg p-2">
                        <div className="text-xs text-gray-600 mb-1">姓名</div>
                        <div className="font-medium text-gray-800 text-sm">
                          {(() => {
                            try {
                              const workflowResultString = sessionStorage.getItem("workflowResult");
                              if (workflowResultString) {
                                const workflowResult = JSON.parse(workflowResultString);
                                return workflowResult.studentName || "未知";
                              }
                            } catch (error) {
                              console.error("獲取學生姓名失敗:", error);
                            }
                            return "未知";
                          })()}
                        </div>
                      </div>

                      {/* 班級 */}
                      <div className="rounded-lg p-2">
                        <div className="text-xs text-gray-600 mb-1">班級</div>
                        <div className="font-medium text-gray-800 text-sm">
                          {(() => {
                            try {
                              const workflowResultString = sessionStorage.getItem("workflowResult");
                              if (workflowResultString) {
                                const workflowResult = JSON.parse(workflowResultString);
                                // 優先使用 className，如果沒有才使用 bookID，最後才是預設值
                                return workflowResult.className || workflowResult.bookID || "未知班級";
                              }
                            } catch (error) {
                              console.error("獲取班級資訊失敗:", error);
                            }
                            return "未知班級";
                          })()}
                        </div>
                      </div>

                      {/* 座號 */}
                      <div className="rounded-lg p-2">
                        <div className="text-xs text-gray-600 mb-1">座號</div>
                        <div className="font-medium text-gray-800 text-sm">
                          {(() => {
                            try {
                              const workflowResultString = sessionStorage.getItem("workflowResult");
                              if (workflowResultString) {
                                const workflowResult = JSON.parse(workflowResultString);
                                return workflowResult.studentSeatNo || "0";
                              }
                            } catch (error) {
                              console.error("獲取座號失敗:", error);
                            }
                            return "0";
                          })()}
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 左側內容區塊 - 根據數據格式顯示不同內容 */}
              <div className="md:col-span-2 space-y-6">
                {/* 字數檢查提示 (兩種模式都顯示，如果有) - 移到最上方 */}
                {evaluationData?.wordCount && (
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-blue-600">字數檢查</h3>
                    </div>
                    <p className="text-gray-700 text-sm">
                      {evaluationData.wordCount}
                    </p>
                  </div>
                )}
                

                {/* 分類評分區塊 - 所有模式都顯示，過濾掉整體評分 */}
                {hasCategories &&
                  evaluationData.categories
                    .filter((category: EvaluationCategory) => category.name !== "整體評分")
                    .map((category: EvaluationCategory, index: number) => {
                      // 該評分項在原始 correctingResult JSON 中存在才可編輯(與舊版可編輯範圍一致)
                      // 兩種格式都要判得出來(kanban 1000517):新格式的四構面在
                      // categories 陣列裡,直讀頂層鍵會讓每個欄位都變唯讀
                      const categoryEditable = Boolean(
                        teacherEditCtx &&
                          hasCategory(teacherEditCtx.correctingResult, category.name)
                      );
                      const categoryRevising = revising && revisionDraft && categoryEditable;
                      const categorySubtitle =
                        category.name === "題旨發揮" ? "能掌握題幹要求，緊扣題旨發揮" :
                        category.name === "資料掌握" ? "能融會貫通題幹資料，舉證詳實" :
                        category.name === "結構安排" ? "結構嚴謹、前後貫通、脈絡清楚" :
                        category.name === "字句應用" ? "標點符號、字句妥切，邏輯清晰" :
                        category.name === "立意取材" ? "主旨明確緊扣題意，取材具體深刻且有層次。" :
                        category.name === "結構組織" ? "結構完整層次分明，起承轉合流暢且呼應。" :
                        category.name === "遣詞造句" ? "用詞精確句型多變，文句通順流暢具節奏。" :
                        category.name === "錯別字、格式與標點符號" ? "字句標點錯誤極少，格式正確且閱讀流暢。" : "";
                      return (
                        <div
                          key={index}
                          className="bg-white rounded-lg shadow-sm p-6"
                        >
                          <div className="mb-2">
                            <h3 className="font-medium text-blue-600">
                              {category.name}－－{categorySubtitle}
                              {!categoryRevising &&
                                renderCategoryLabel(category) &&
                                `：${renderCategoryLabel(category)}`}
                            </h3>
                            {categoryRevising && revisionDraft && (
                              // 修訂模式:得分變成虛線數字輸入框
                              <div className="mt-2">
                                <RevisionFieldWrap fieldId={`cat-score:${category.name}`}>
                                  <RevisionScoreInput
                                    value={revisionDraft.catScores[category.name] ?? ""}
                                    onChange={(value) =>
                                      updateRevisionDraft((draft) => ({
                                        ...draft,
                                        catScores: { ...draft.catScores, [category.name]: value },
                                      }))
                                    }
                                    ariaLabel={`${category.name}得分`}
                                    max={category.maxScore}
                                    error={revisionErrors[`cat-score:${category.name}`]}
                                    disabled={revisionSaving}
                                  />
                                </RevisionFieldWrap>
                              </div>
                            )}
                          </div>
                          {categoryRevising && revisionDraft ? (
                            // 修訂模式:評語變成虛線 textarea
                            <RevisionFieldWrap fieldId={`cat-comment:${category.name}`}>
                              <RevisionTextarea
                                value={revisionDraft.catComments[category.name] ?? ""}
                                onChange={(value) =>
                                  updateRevisionDraft((draft) => ({
                                    ...draft,
                                    catComments: { ...draft.catComments, [category.name]: value },
                                  }))
                                }
                                ariaLabel={`${category.name}評語`}
                                rows={4}
                                placeholder={`${category.name}評語`}
                                error={revisionErrors[`cat-comment:${category.name}`]}
                                disabled={revisionSaving}
                              />
                            </RevisionFieldWrap>
                          ) : (
                            <p className="text-gray-700 text-sm">
                              {category.comment}
                            </p>
                          )}
                        </div>
                      );
                    })}


                {/* 若無分類評分數據，但有整體評語，顯示整體評語和建議 */}
                {!hasCategories && (
                  <>
                    {/* 整體評語(老師模式即使空評語也顯示卡片供編輯) */}
                    {(evaluationData?.overallComment || teacherEditCtx) && (
                      <div className="bg-white rounded-lg shadow-sm p-6 relative">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-medium text-blue-600">
                            ✅ 整體評語
                          </h3>
                        </div>
                        {renderOverallComment()}
                      </div>
                    )}

                    {/* 建議列表(老師模式即使空清單也顯示卡片供新增) */}
                    {((evaluationData?.suggestions &&
                      evaluationData.suggestions.length > 0) ||
                      teacherEditCtx) && (
                        <div className="bg-white rounded-lg shadow-sm p-6">
                          <div className="flex justify-between items-center mb-2">
                            <h3 className="font-medium text-blue-600">建議</h3>
                          </div>
                          <ul className={revising ? "space-y-2" : "list-disc pl-5 space-y-2"}>
                            {suggestionList.map(
                              (suggestion: string, index: number) => (
                                <li
                                  key={index}
                                  className="text-gray-700 text-sm"
                                >
                                  {renderSuggestionRow(index, <>{suggestion}</>)}
                                </li>
                              )
                            )}
                          </ul>
                          {renderSuggestionExtras(suggestionList.length)}
                        </div>
                      )}
                  </>
                )}

                {/* 評分標準表格 - 從 API 獲取，低調樣式 */}
                {evaluationData && sessionStorage.getItem("workflowResult") && JSON.parse(sessionStorage.getItem("workflowResult") || "{}").markdown && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mt-8">
                    <div className="opacity-75">
                      <MarkdownTable markdown={JSON.parse(sessionStorage.getItem("workflowResult") || "{}").markdown} />
                    </div>
                  </div>
                )}
              </div>

              {/* 右側內容區塊 - 使用修改後的評分顯示 */}
              <div className="space-y-6">

                {/* 如果有分類評分，在右側顯示整體評語和建議 */}
                {hasCategories ? (
                  <>
                    {/* 等級顯示 - 帶整體評語(老師模式即使空評語也顯示卡片供編輯) */}
                    {(evaluationData?.overallComment || teacherEditCtx) && (
                      <div className="bg-white rounded-lg shadow-sm p-6 relative">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-medium text-blue-600">
                            ✅ 整體評語
                          </h3>
                          {evaluationData?.totalGrade && evaluationData.totalGrade.trim() && (
                            <span
                              className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center"
                              style={{
                                backgroundColor: getGradeColor(evaluationData.totalGrade),
                              }}
                            >
                              {evaluationData.totalGrade}
                            </span>
                          )}
                        </div>
                        {renderOverallComment()}
                      </div>
                    )}

                    {/* 建議列表(老師模式即使空清單也顯示卡片供新增) */}
                    {((evaluationData?.suggestions &&
                      evaluationData.suggestions.length > 0) ||
                      teacherEditCtx) && (
                        <div className="bg-white rounded-lg shadow-sm p-6">
                          <div className="flex justify-between items-center mb-2">
                            <h3 className="font-medium text-blue-600">💡 寫作建議</h3>
                          </div>
                          <ul className={revising ? "space-y-2" : "list-disc pl-5 space-y-1"}>
                            {suggestionList.map(
                              (suggestion, index) => (
                                <li
                                  key={index}
                                  className="text-gray-700 text-sm [&_strong]:font-bold [&_em]:italic"
                                >
                                  {renderSuggestionRow(
                                    index,
                                    <ReactMarkdown
                                      components={{
                                        p: ({ children }) => <span>{children}</span>,
                                      }}
                                    >
                                      {suggestion}
                                    </ReactMarkdown>
                                  )}
                                </li>
                              )
                            )}
                          </ul>
                          {renderSuggestionExtras(suggestionList.length)}
                        </div>
                      )}
                  </>
                ) : (
                  /* 如果沒有分類評分，在右側顯示等第區塊 */
                  <div className="rounded-lg shadow-sm p-6"></div>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* 頁腳 */}
        <Footer />
      </div>

      {/* 修訂模式底部漸層提示條:頁面層級視覺提示，與工具列上的取消/儲存按鈕位置無關，
          維持原本掛載位置不動(不隨按鈕移進工具列) */}
      {teacherEditCtx && revising && <RevisionFoot />}

      {/* 修訂模式站內自製彈窗(訊息／確認共用一個掛載點) */}
      {revisionPopup && (
        <RevisionPopup
          message={revisionPopup.message}
          autoCloseMs={revisionPopup.autoCloseMs}
          onConfirm={
            revisionPopup.onConfirm
              ? () => {
                  const confirmed = revisionPopup.onConfirm;
                  setRevisionPopup(null);
                  confirmed?.();
                }
              : undefined
          }
          onClose={() => setRevisionPopup(null)}
        />
      )}

      {/* 題目內容 Popup */}
      {showQuestionPopup && currentChapter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{zIndex: 10000}}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {currentChapter.chapter}
              </h2>
              <button
                onClick={() => setShowQuestionPopup(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            {/* 題目內容 */}
            {currentChapter.chapterDesc && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">題目內容</h3>
                <div className="text-gray-700 text-sm leading-relaxed">
                  <TopicDesc
                    desc={currentChapter.chapterDesc}
                    images={currentChapter.images}
                    chapterName={currentChapter.chapter}
                  />
                </div>
                
                {/* 顯示章節圖片 */}
                {currentChapter.chapterDescUrl && (
                  <div className="mt-4">
                    <img
                      src={currentChapter.chapterDescUrl}
                      alt={`${currentChapter.chapter} - 章節圖片`}
                      className="w-full max-w-2xl mx-auto rounded-md border border-gray-200 shadow-sm"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowQuestionPopup(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 圖片放大 popup */}
      {showImagePopup && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-75 backdrop-blur-sm"
          onClick={() => setShowImagePopup(false)}
        >
          <div
            className="flex h-full w-full items-center justify-center px-4 py-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="relative flex w-full items-center justify-center"
              style={{
                maxWidth: 'calc(100vw - 48px)',
                maxHeight: 'calc(100vh - 48px)',
              }}
            >
              <div
                className={`relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-black/40 shadow-2xl ${imageZoomLevel > 1 ? (isImageDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in'}`}
                style={{
                  touchAction: imageZoomLevel > 1 ? 'none' : 'auto',
                  maxWidth: 'calc(100vw - 48px)',
                  maxHeight: 'calc(100vh - 48px)',
                }}
                ref={imageContainerRef}
                onPointerDown={handleImagePointerDown}
                onPointerMove={handleImagePointerMove}
                onPointerUp={handleImagePointerUp}
                onPointerLeave={handleImagePointerCancel}
                onPointerCancel={handleImagePointerCancel}
                onClick={handleImageClick}
              >
                <img
                  src={popupImageUrl}
                  alt="成績單圖片"
                  className="block select-none object-contain"
                  ref={popupImageRef}
                  style={{
                    maxHeight: 'calc(100vh - 48px)',
                    maxWidth: 'calc(100vw - 48px)',
                    transform:
                      imageZoomLevel === 1
                        ? 'none'
                        : `translate(${imageOffset.x / imageZoomLevel}px, ${imageOffset.y / imageZoomLevel}px) scale(${imageZoomLevel})`,
                    transition: dragStateRef.current.isDragging ? 'none' : 'transform 0.2s ease',
                    transformOrigin: 'center center',
                  }}
                  draggable={false}
                />
              </div>

              <button
                onClick={() => setShowImagePopup(false)}
                className="absolute right-4 top-4 rounded-full bg-black bg-opacity-50 p-2 text-white transition-all hover:bg-opacity-75"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 錯誤彈出視窗元件 */}
      {errorPopup.show && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.75)" }}
        >
          <div className="bg-white rounded-lg shadow-md max-w-md w-full mx-4 overflow-hidden border border-gray-200">
            <div className="bg-[#254D71] px-4 py-3 text-white flex justify-between items-center">
              <h3 className="font-medium text-lg">提示</h3>
              <button
                onClick={() => {
                  setErrorPopup({ show: false, message: "" });
                  // 追蹤錯誤視窗關閉
                  trackEvent('error_popup_closed', {
                    error_message: errorPopup.message
                  });
                }}
                className="text-white hover:text-gray-200"
              >
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-[#2D86FF]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-gray-700">{errorPopup.message}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => {
                  setErrorPopup({ show: false, message: "" });
                  // 追蹤錯誤視窗確認按鈕點擊
                  trackEvent('error_popup_confirmed', {
                    error_message: errorPopup.message
                  });
                }}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2D86FF] text-base font-medium text-white hover:bg-blue-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加動畫樣式 */}
      <style>{`
        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.25rem;
        }

        .loading span {
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 50%;
          background-color: white;
          opacity: 0.7;
          animation: loading 1s infinite alternate ease-in-out;
        }

        .loading span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .loading span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes loading {
          0% {
            transform: scale(0.5);
            opacity: 0.3;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
